import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  getAdminStats, updateFlags, getAdminAnalyses, getAdminDebug, getAdminOcrUsage,
  getAdminUsers, getAdminCalibration, setAdminActualGrade,
  createPromo, listPromo, togglePromo, deletePromo, grandfatherBatch,
  getCalibrationDashboard, getCalibrationRecords, createCalibrationRecord,
  updateCalibrationRecord, deleteCalibrationRecord, getCalibrationSettings, setCalibrationSettings,
  getCalibrationVersions, createCalibrationVersion, activateCalibrationVersion,
  previewCalibration, getCalibrationWeekly, getLastRegression, runRegression,
  getCalibrationHealth, sendWeeklyOpsReport, getLastOpsReportSend, checkDriftAlerts,
  getWeeklyOpsReport,
  getRewardsConfig, setRewardsConfig, getRewardsDashboard, runPrizeDraw, getPrizeHistory,
  getPromoMode, updatePromoMode,
} from "../lib/api";
import BenchmarkPanel from "../components/admin/BenchmarkPanel";
import ProvenancePanel from "../components/admin/ProvenancePanel";
import BandCalibrationPanel from "../components/admin/BandCalibrationPanel";
import PricingPlansPanel from "../components/admin/PricingPlansPanel";
import PartnersPanel from "../components/admin/PartnersPanel";
import CommissionsPanel from "../components/admin/CommissionsPanel";
import UserProModal from "../components/admin/UserProModal";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, Loader2, Database, Users, Target, GitCompare, ShieldAlert,
  DollarSign, FileText, AlertTriangle, Clock, Bug, X, Check, TrendingUp, Cpu, Ticket, Plus,
  Sliders, Trash2, PenSquare, ArrowUp, ArrowDown, GitBranch, PlayCircle, CalendarClock,
  Eye, RefreshCw, History, Save, Activity, Mail, Bell,
  Award, Gift, Trophy, Sparkles, Search, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) => `£${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usd = (n) => `$${Number(n || 0).toFixed(n < 1 ? 4 : 2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="bg-card border border-border p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground mb-2">
      <Icon size={15} /> <span className="overline">{label}</span>
    </div>
    <p className="font-mono text-3xl font-semibold text-oxford tabular-nums break-all">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const FlagRow = ({ label, desc, on, disabled, onToggle, testId }) => (
  <div className="flex items-center justify-between py-4 border-b border-border last:border-0">
    <div className="pr-6">
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
    </div>
    <button onClick={onToggle} disabled={disabled} data-testid={testId}
      className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${on ? "bg-oxford" : "bg-muted-foreground/30"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${on ? "translate-x-6" : ""}`} />
    </button>
  </div>
);

const TABS = ["Overview", "AI Log", "Calibration", "Calibration Centre", "Pricing", "Partners", "Commissions", "Rewards", "Promo", "Users", "Search", "Health", "Providers", "Benchmark", "Provenance"];

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analyses, setAnalyses] = useState(null);
  const [calib, setCalib] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [userFilter, setUserFilter] = useState("");
  const [proModalUser, setProModalUser] = useState(null);
  const [promo, setPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    code: "", benefit_type: "pro_days", days: 90, credits: 5,
    fixed_term_days: 30, discount_pct: 30,
    max_uses: 1, expires_at: "", note: "",
    campaign: "manual", assigned_email: "",
  });
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [gfBusy, setGfBusy] = useState(false);
  const [gfPreview, setGfPreview] = useState(null);
  // Launch mode — "Free until further notice" toggle
  const [launchMode, setLaunchMode] = useState(null);
  const [launchSaving, setLaunchSaving] = useState(false);
  const [searchBudget, setSearchBudget] = useState(null);
  const [searchUsage, setSearchUsage] = useState(null);
  const [searchHealth, setSearchHealth] = useState(null);
  const [savingBudget, setSavingBudget] = useState(false);
  const [searchCost, setSearchCost] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [turnitinBenchmarks, setTurnitinBenchmarks] = useState(null);
  const [uploadingBenchmark, setUploadingBenchmark] = useState(false);
  const [benchmarkMyaePct, setBenchmarkMyaePct] = useState("");
  const [mark, setMark] = useState(null); // { id } for the actual-mark dialog
  const [markValue, setMarkValue] = useState("");
  const [markErr, setMarkErr] = useState("");
  const [markSaving, setMarkSaving] = useState(false);
  const [debug, setDebug] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  // Calibration Centre state
  const [calDash, setCalDash] = useState(null);
  const [calRecords, setCalRecords] = useState(null);
  const [calSettings, setCalSettings] = useState(null);
  const [calSection, setCalSection] = useState("Dashboard");  // Dashboard | Records | Versions | Weekly | Settings
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualForm, setManualForm] = useState({
    assignment_title: "", subject: "", module: "", module_level: "", assessment_type: "",
    university: "", faculty: "", department: "",
    actual_lecturer_mark: "", myae_raw_prediction: "", ai_confidence: "",
    lecturer_feedback: "", notes: "",
  });
  const [manualSaving, setManualSaving] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null); // record being edited
  const [ocrUsage, setOcrUsage] = useState(null);
  // Calibration versioning + preview + weekly cycle state
  const [calVersions, setCalVersions] = useState(null);
  const [calWeekly, setCalWeekly] = useState(null);
  const [calRegression, setCalRegression] = useState(null);
  const [runningRegression, setRunningRegression] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAdjustments, setPreviewAdjustments] = useState(null); // draft edits
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [newVersionNotes, setNewVersionNotes] = useState("");
  const [newVersionActivate, setNewVersionActivate] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);
  // Drift monitoring + Weekly Ops Report state
  const [calHealth, setCalHealth] = useState(null);
  const [lastOpsSend, setLastOpsSend] = useState(null);
  const [opsReport, setOpsReport] = useState(null);  // TL;DR + since_last_week preview
  const [sendingOpsReport, setSendingOpsReport] = useState(false);
  const [checkingDrift, setCheckingDrift] = useState(false);
  // Rewards state
  const [rewardsConfig, setRewardsConfigState] = useState(null);
  const [rewardsDash, setRewardsDash] = useState(null);
  const [prizeHistory, setPrizeHistory] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [drawPrize, setDrawPrize] = useState("");
  const [drawNotes, setDrawNotes] = useState("");

  const load = () => getAdminStats().then(setStats).catch(() => setErr(true));
  useEffect(() => { load(); getAdminOcrUsage().then(setOcrUsage).catch(() => {}); axios.get(`${API}/admin/search-cost`, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } }).then((r) => setSearchCost(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (tab === "AI Log" && !analyses) getAdminAnalyses({ limit: 100 }).then(setAnalyses).catch(() => {});
    if (tab === "Calibration" && !calib) getAdminCalibration().then(setCalib).catch(() => {});
    if (tab === "Calibration Centre") {
      if (!calDash) getCalibrationDashboard().then(setCalDash).catch(() => {});
      if (!calRecords) getCalibrationRecords().then(setCalRecords).catch(() => {});
      if (!calSettings) getCalibrationSettings().then(setCalSettings).catch(() => {});
      if (!calVersions) getCalibrationVersions().then(setCalVersions).catch(() => {});
      if (!calWeekly) getCalibrationWeekly().then(setCalWeekly).catch(() => {});
      if (!calRegression) getLastRegression().then(setCalRegression).catch(() => {});
      if (!calHealth) getCalibrationHealth().then(setCalHealth).catch(() => {});
      if (!lastOpsSend) getLastOpsReportSend().then(setLastOpsSend).catch(() => {});
      if (!opsReport) getWeeklyOpsReport().then(setOpsReport).catch(() => {});
    }
    if (tab === "Users" && !usersData) getAdminUsers().then(setUsersData).catch(() => {});
    if (tab === "Promo" && !promo) getAdminPromo();
    if (tab === "Promo" && !launchMode) getPromoMode().then(setLaunchMode).catch(() => {});
    if (tab === "Rewards") {
      if (!rewardsConfig) getRewardsConfig().then(setRewardsConfigState).catch(() => {});
      if (!rewardsDash) getRewardsDashboard().then(setRewardsDash).catch(() => {});
      if (!prizeHistory) getPrizeHistory().then(setPrizeHistory).catch(() => {});
    }
    if (tab === "Search") {
      if (!searchBudget) axios.get(`${API}/admin/search-budget`, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } }).then((r) => setSearchBudget(r.data)).catch(() => {});
      if (!searchUsage) axios.get(`${API}/admin/search-usage`, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } }).then((r) => setSearchUsage(r.data)).catch(() => {});
      if (!searchHealth) axios.get(`${API}/submission-intelligence/providers/health`, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } }).then((r) => setSearchHealth(r.data)).catch(() => {});
    }
    if (tab === "Health") {
      axios.get(`${API}/admin/health`, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } }).then((r) => setHealthData(r.data)).catch(() => {});
      axios.get(`${API}/admin/turnitin-benchmarks`, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } }).then((r) => setTurnitinBenchmarks(r.data)).catch(() => {});
    }
  }, [tab]); // eslint-disable-line

  const reloadCalibration = () => {
    getCalibrationDashboard().then(setCalDash).catch(() => {});
    getCalibrationRecords().then(setCalRecords).catch(() => {});
    getCalibrationSettings().then(setCalSettings).catch(() => {});
    getCalibrationVersions().then(setCalVersions).catch(() => {});
    getCalibrationWeekly().then(setCalWeekly).catch(() => {});
    getCalibrationHealth().then(setCalHealth).catch(() => {});
    getLastOpsReportSend().then(setLastOpsSend).catch(() => {});
    getWeeklyOpsReport().then(setOpsReport).catch(() => {});
  };

  const getAdminPromo = (camp) => listPromo(camp || campaignFilter || undefined).then(setPromo).catch(() => {});

  const toggle = async (key) => {
    if (!stats) return;
    setSaving(true);
    try {
      const next = { ...stats.feature_flags, [key]: !stats.feature_flags[key] };
      const flags = await updateFlags(next);
      setStats({ ...stats, feature_flags: flags });
      toast.success("Feature flag updated.");
    } catch (e) { toast.error("Could not update flag."); } finally { setSaving(false); }
  };

  const openDebug = async (id) => {
    setDebugLoading(true); setDebug({ id });
    try { setDebug(await getAdminDebug(id)); }
    catch (e) { toast.error("Could not load debug data."); setDebug(null); }
    finally { setDebugLoading(false); }
  };

  const openMark = (id) => { setMark({ id }); setMarkValue(""); setMarkErr(""); };
  const saveMark = async () => {
    const num = parseFloat(markValue);
    if (isNaN(num) || num < 0 || num > 100) { setMarkErr("Enter a number between 0 and 100."); return; }
    setMarkSaving(true);
    try {
      const res = await setAdminActualGrade(mark.id, num);
      toast.success(`Saved — predicted ${res.predicted ?? "—"} vs actual ${res.actual} (Δ ${res.difference ?? "—"})`);
      setMark(null);
      getAdminAnalyses({ limit: 100 }).then(setAnalyses).catch(() => {});
      setCalib(null); load();
    } catch (e) { setMarkErr(e?.response?.data?.detail || "Could not save actual grade."); }
    finally { setMarkSaving(false); }
  };

  const submitPromo = async () => {
    setCreatingPromo(true);
    try {
      const payload = {
        code: promoForm.code.trim(),
        benefit_type: promoForm.benefit_type,
        max_uses: Number(promoForm.max_uses) || 1,
        expires_at: promoForm.expires_at || "",
        note: promoForm.note.trim(),
        campaign: (promoForm.campaign || "manual").trim().toLowerCase() || "manual",
        assigned_email: promoForm.assigned_email.trim().toLowerCase() || "",
      };
      if (promoForm.benefit_type === "credits") payload.credits = Number(promoForm.credits);
      else if (promoForm.benefit_type === "pro_days") payload.days = Number(promoForm.days);
      else if (promoForm.benefit_type === "fixed_term_ultra_pro") payload.fixed_term_days = Number(promoForm.fixed_term_days);
      else if (promoForm.benefit_type === "percentage_discount") payload.discount_pct = Number(promoForm.discount_pct);
      // lifetime_ultra_pro needs no extra fields
      const res = await createPromo(payload);
      toast.success(`Promo code created: ${res.code}`);
      setPromoForm({
        code: "", benefit_type: "pro_days", days: 90, credits: 5,
        fixed_term_days: 30, discount_pct: 30,
        max_uses: 1, expires_at: "", note: "",
        campaign: "manual", assigned_email: "",
      });
      getAdminPromo();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create promo code."); }
    finally { setCreatingPromo(false); }
  };

  const previewGrandfather = async () => {
    setGfBusy(true);
    try {
      const res = await grandfatherBatch({
        benefit_type: "lifetime_ultra_pro",
        campaign: "grandfather_ultra_pro_beta",
        criteria: { active_pro: true },
        dry_run: true,
      });
      setGfPreview(res);
      toast.message(`${res.will_issue} code${res.will_issue === 1 ? "" : "s"} will be issued (${res.already_issued} already have one).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed."); }
    finally { setGfBusy(false); }
  };

  const runGrandfather = async () => {
    if (!window.confirm(`Issue lifetime Ultra Pro codes to ${gfPreview?.will_issue || 0} Pro subscribers now? This cannot be un-done automatically.`)) return;
    setGfBusy(true);
    try {
      const res = await grandfatherBatch({
        benefit_type: "lifetime_ultra_pro",
        campaign: "grandfather_ultra_pro_beta",
        criteria: { active_pro: true },
        note: "Grandfather Ultra Pro Beta cohort",
        dry_run: false,
      });
      toast.success(`Issued ${res.issued} lifetime Ultra Pro codes.`);
      setGfPreview(null);
      getAdminPromo();
    } catch (e) { toast.error(e?.response?.data?.detail || "Batch issuance failed."); }
    finally { setGfBusy(false); }
  };

  const flipPromo = async (code, active) => {
    try { await togglePromo(code, active); getAdminPromo(); }
    catch (e) { toast.error("Could not update code."); }
  };

  const removePromo = async (code) => {
    if (!window.confirm(`Delete promo code ${code}? Redemption history will be kept for audit, but the code will stop working immediately.`)) return;
    try { await deletePromo(code); toast.success(`Deleted ${code}`); getAdminPromo(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete code."); }
  };

  // ---- Calibration Centre handlers ----
  const saveCalSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const next = await setCalibrationSettings(patch);
      setCalSettings(next);
      toast.success("Calibration settings updated.");
      // Refresh dashboard so bucket_readiness reflects new min_samples
      getCalibrationDashboard().then(setCalDash).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update settings."); }
    finally { setSavingSettings(false); }
  };

  const saveManualEntry = async () => {
    const actual = parseFloat(manualForm.actual_lecturer_mark);
    const raw = parseFloat(manualForm.myae_raw_prediction);
    if (isNaN(actual) || actual < 0 || actual > 100) { toast.error("Enter a lecturer mark between 0 and 100."); return; }
    if (isNaN(raw) || raw < 0 || raw > 100) { toast.error("Enter a raw MyAE prediction between 0 and 100."); return; }
    setManualSaving(true);
    try {
      const payload = {
        ...manualForm,
        actual_lecturer_mark: actual, myae_raw_prediction: raw,
        ai_confidence: manualForm.ai_confidence ? parseFloat(manualForm.ai_confidence) : null,
      };
      await createCalibrationRecord(payload);
      toast.success("Calibration record added.");
      setShowManualEntry(false);
      setManualForm({
        assignment_title: "", subject: "", module: "", module_level: "", assessment_type: "",
        university: "", faculty: "", department: "",
        actual_lecturer_mark: "", myae_raw_prediction: "", ai_confidence: "",
        lecturer_feedback: "", notes: "",
      });
      reloadCalibration();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save record."); }
    finally { setManualSaving(false); }
  };

  const saveRecordEdit = async () => {
    if (!editingRecord) return;
    setManualSaving(true);
    try {
      const patch = {
        module: editingRecord.module, subject: editingRecord.subject,
        module_level: editingRecord.module_level, assessment_type: editingRecord.assessment_type,
        university: editingRecord.university, faculty: editingRecord.faculty,
        department: editingRecord.department,
        actual_lecturer_mark: editingRecord.actual_lecturer_mark != null && editingRecord.actual_lecturer_mark !== "" ? parseFloat(editingRecord.actual_lecturer_mark) : null,
        lecturer_feedback: editingRecord.lecturer_feedback, notes: editingRecord.notes,
      };
      await updateCalibrationRecord(editingRecord.id, patch);
      toast.success("Record updated.");
      setEditingRecord(null);
      reloadCalibration();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
    finally { setManualSaving(false); }
  };

  const removeRecord = async (id) => {
    if (!window.confirm("Delete this calibration record? This cannot be undone.")) return;
    try { await deleteCalibrationRecord(id); toast.success("Record deleted."); reloadCalibration(); }
    catch (e) { toast.error("Could not delete record."); }
  };

  // ---- Preview / Versioning / Regression handlers ----
  const openPreview = async (adjustments = null) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewResult(null);
    setPreviewAdjustments(adjustments || { ...(calSettings?.assessment_type_adjustments || {}) });
    try {
      const res = await previewCalibration({ assessment_type_adjustments: adjustments });
      setPreviewResult(res);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed."); }
    finally { setPreviewLoading(false); }
  };

  const refreshPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await previewCalibration({ assessment_type_adjustments: previewAdjustments });
      setPreviewResult(res);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed."); }
    finally { setPreviewLoading(false); }
  };

  const applyAsNewVersion = async (activate) => {
    setSavingVersion(true);
    try {
      const payload = {
        assessment_type_adjustments: previewAdjustments,
        notes: newVersionNotes.trim(),
        activate,
      };
      await createCalibrationVersion(payload);
      toast.success(activate ? "New calibration version activated." : "New calibration version saved.");
      setPreviewOpen(false);
      setNewVersionNotes("");
      setNewVersionActivate(false);
      setNewVersionOpen(false);
      reloadCalibration();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save version."); }
    finally { setSavingVersion(false); }
  };

  const activateVersion = async (version) => {
    if (!window.confirm(`Activate calibration version ${version}? Its adjustments will apply to all new analyses immediately.`)) return;
    try {
      await activateCalibrationVersion(version);
      toast.success(`Version ${version} activated.`);
      reloadCalibration();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not activate version."); }
  };

  const doRunRegression = async () => {
    setRunningRegression(true);
    try {
      const res = await runRegression();
      setCalRegression(res);
      if (res.status === "pass") toast.success(`Regression PASS · ${res.tests_total} tests · ${res.runtime_s}s`);
      else toast.error(`Regression ${res.status?.toUpperCase()} · ${res.tests_failed ?? "?"} failed`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Regression run failed."); }
    finally { setRunningRegression(false); }
  };

  const doSendOpsReport = async () => {
    if (!window.confirm("Send the Weekly Ops Report NOW to admin + configured recipients?")) return;
    setSendingOpsReport(true);
    try {
      const res = await sendWeeklyOpsReport();
      toast.success(`Ops report sent to ${res.sent_to.length} recipient(s).`);
      getLastOpsReportSend().then(setLastOpsSend).catch(() => {});
      getWeeklyOpsReport().then(setOpsReport).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed."); }
    finally { setSendingOpsReport(false); }
  };

  const doCheckDrift = async () => {
    setCheckingDrift(true);
    try {
      const res = await checkDriftAlerts();
      if (res.sent) toast.success(`Drift alert sent to ${res.recipients.length} recipient(s) — ${res.red_count} RED bucket(s).`);
      else if (res.reason === "no_red_buckets") toast.success("No drift alerts — all buckets healthy.");
      else toast(`Alert skipped: ${res.reason}`);
      getCalibrationHealth().then(setCalHealth).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Check failed."); }
    finally { setCheckingDrift(false); }
  };

  // Rewards handlers
  const saveRewardsField = async (patch) => {
    try {
      const r = await setRewardsConfig(patch);
      setRewardsConfigState(r);
      toast.success("Rewards config updated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const doPrizeDraw = async () => {
    if (!drawPrize.trim()) { toast.error("Enter a prize name first."); return; }
    if (!window.confirm(`Draw a winner for ${rewardsDash?.month_key || "this month"} for prize "${drawPrize.trim()}"?`)) return;
    setDrawing(true);
    try {
      const r = await runPrizeDraw({ prize: drawPrize.trim(), notes: drawNotes.trim() });
      toast.success(`Winner drawn: ${r.winner_email || r.winner_name || r.winner_user_id}`);
      setDrawPrize(""); setDrawNotes("");
      getRewardsDashboard().then(setRewardsDash).catch(() => {});
      getPrizeHistory().then(setPrizeHistory).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Draw failed."); }
    finally { setDrawing(false); }
  };

  if (err || (user && user.role !== "admin")) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-muted-foreground" data-testid="admin-denied">
      <ShieldAlert size={28} /> <p>Admin access required.</p>
      <button onClick={() => navigate("/")} className="text-oxford hover:underline text-sm">Back to MyAE</button>
    </div>
  );
  if (!stats) return <div className="min-h-screen flex items-center justify-center gap-3 text-muted-foreground"><Loader2 className="animate-spin" /> Loading…</div>;

  const cal = stats.calibration || {};
  const sim = stats.similarity || {};
  const rev = stats.revenue || {};
  const aic = stats.ai_cost || {};

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <div className="border-b border-border bg-card px-6 md:px-8 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-oxford hover:underline" data-testid="admin-back-btn">
          <ArrowLeft size={16} /> Back
        </button>
        <span className="text-sm text-muted-foreground">Administrator</span>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <p className="overline text-oxford mb-2">Internal</p>
        <h1 className="font-serif text-5xl tracking-tight mb-6">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-border mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === t ? "border-oxford text-oxford" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t}
            </button>
          ))}
        </div>

        {/* ---------------- OVERVIEW ---------------- */}
        {tab === "Overview" && (
          <div data-testid="admin-overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-8">
              <Stat icon={Users} label="Total users" value={stats.total_users} sub="registered accounts" testId="stat-users" />
              <Stat icon={GitCompare} label="Total analyses" value={stats.total_analyses} sub="completed reports" testId="stat-analyses" />
              <Stat icon={FileText} label="Documents processed" value={stats.documents_uploaded} sub="briefs, guides, submissions" testId="stat-documents" />
              <Stat icon={DollarSign} label="Stripe revenue" value={money(rev.total_gbp)} sub={`${rev.paid_transactions || 0} paid transactions`} testId="stat-revenue" />
              <Stat icon={Cpu} label="AI cost (est.)" value={usd(aic.total_usd)} sub={`≈ ${usd(aic.avg_per_analysis_usd)} / analysis`} testId="stat-aicost" />
              <Stat icon={AlertTriangle} label="Errors" value={stats.error_count} sub="failed analyses" testId="stat-errors" />
              <Stat icon={Clock} label="Avg processing" value={stats.avg_processing_time_s != null ? `${stats.avg_processing_time_s}s` : "—"} sub="per analysis" testId="stat-processing" />
              <Stat icon={Database} label="Corpus size" value={stats.corpus_size} sub={`opt-in ${stats.opt_in_rate ?? 0}%`} testId="stat-corpus" />
            </div>

            {ocrUsage && (
              <div className="bg-card border border-border p-6 mb-8" data-testid="overview-ocr">
                <div className="flex items-center justify-between mb-3">
                  <p className="overline text-muted-foreground flex items-center gap-1.5">
                    <FileText size={14} /> OCR pipeline (Google Vision)
                  </p>
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 border ${ocrUsage.enabled ? "border-[#15803D] text-[#15803D] bg-secondary" : "border-border text-muted-foreground"}`}>
                    {ocrUsage.enabled ? "Active" : "Not configured"}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div><p className="font-mono text-2xl font-semibold text-oxford">{ocrUsage.totals.pages_this_month}</p><p className="text-xs text-muted-foreground">pages this month</p></div>
                  <div><p className="font-mono text-2xl font-semibold text-oxford">{ocrUsage.totals.documents_this_month}</p><p className="text-xs text-muted-foreground">docs OCR&#39;d</p></div>
                  <div><p className="font-mono text-2xl font-semibold text-oxford">{ocrUsage.totals.avg_confidence_this_month != null ? `${Math.round(ocrUsage.totals.avg_confidence_this_month * 100)}%` : "—"}</p><p className="text-xs text-muted-foreground">avg confidence</p></div>
                  <div><p className="font-mono text-2xl font-semibold text-oxford">{ocrUsage.totals.free_tier_headroom}</p><p className="text-xs text-muted-foreground">free-tier pages left</p></div>
                </div>
                {!ocrUsage.enabled && (
                  <p className="text-xs text-muted-foreground mt-3">Add <code className="font-mono bg-secondary px-1 py-0.5">GCP_SERVICE_ACCOUNT_JSON</code> and <code className="font-mono bg-secondary px-1 py-0.5">GCP_PROJECT_ID</code> in Emergent Secrets to enable OCR on image-heavy documents.</p>
                )}
                {ocrUsage.enabled && ocrUsage.totals.pages_this_month > 800 && (
                  <p className="text-xs text-amber-700 mt-3">⚠️ You&#39;ve used {ocrUsage.totals.pages_this_month} pages this month — free tier is 1,000/month. Additional pages cost ~£0.0012 each.</p>
                )}
              </div>
            )}

            {/* Submission Intelligence — search API burn rate */}
            {searchCost && (
              <div className="bg-card border border-border p-6 mb-8" data-testid="overview-search-cost">
                <div className="flex items-center justify-between mb-3">
                  <p className="overline text-muted-foreground flex items-center gap-1.5">
                    <Search size={14} /> Submission Intelligence · search API spend
                  </p>
                  <button onClick={() => setTab("Search")}
                    data-testid="overview-search-cost-link"
                    className="text-[11px] uppercase tracking-wider text-oxford hover:underline">Manage →</button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="font-mono text-2xl font-semibold text-oxford">£{(searchCost.last_24h?.cost_gbp ?? 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">last 24h</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{searchCost.last_24h?.brave || 0} brave · {searchCost.last_24h?.exa || 0} exa</p>
                  </div>
                  <div>
                    <p className="font-mono text-2xl font-semibold text-oxford">£{(searchCost.last_7d?.cost_gbp ?? 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">last 7 days</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{searchCost.last_7d?.brave || 0} brave · {searchCost.last_7d?.exa || 0} exa</p>
                  </div>
                  <div>
                    <p className="font-mono text-2xl font-semibold text-oxford">£{(searchCost.last_30d?.cost_gbp ?? 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">last 30 days</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{searchCost.last_30d?.brave || 0} brave · {searchCost.last_30d?.exa || 0} exa</p>
                  </div>
                  <div>
                    <p className="font-mono text-2xl font-semibold text-indigo-700">£{(searchCost.projected_monthly_gbp ?? 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">projected / month</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">from last 7 days</p>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">
                  Unit prices (editable in Search tab): Brave £{searchCost.unit_prices_gbp?.brave} · Exa £{searchCost.unit_prices_gbp?.exa} per query. Update to reflect your actual plan pricing.
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-card border border-border p-6" data-testid="overview-calibration">
                <p className="overline text-muted-foreground mb-3 flex items-center gap-1.5"><Target size={14} /> Prediction accuracy</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="font-mono text-2xl font-semibold text-oxford">{cal.average_prediction_error != null ? `±${cal.average_prediction_error}%` : "—"}</p><p className="text-xs text-muted-foreground">avg error</p></div>
                  <div><p className="font-mono text-2xl font-semibold text-oxford">{cal.validated_assignments || 0}</p><p className="text-xs text-muted-foreground">validated marks</p></div>
                  <div><p className="font-mono text-2xl font-semibold text-oxford">{cal.within_5_percentage != null ? `${cal.within_5_percentage}%` : "—"}</p><p className="text-xs text-muted-foreground">within ±5%</p></div>
                  <div><p className="font-mono text-2xl font-semibold text-oxford">{cal.within_10_percentage != null ? `${cal.within_10_percentage}%` : "—"}</p><p className="text-xs text-muted-foreground">within ±10%</p></div>
                </div>
              </div>
              <div className="bg-card border border-border p-6" data-testid="overview-themes">
                <p className="overline text-muted-foreground mb-3 flex items-center gap-1.5"><TrendingUp size={14} /> Most common feedback themes</p>
                {(stats.feedback_themes || []).length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
                {(stats.feedback_themes || []).map((t) => (
                  <div key={t.theme} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                    <span className="text-sm">{t.theme}</span>
                    <span className="font-mono text-sm text-muted-foreground tabular-nums">{t.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border p-6" data-testid="overview-prompt-versions">
              <p className="overline text-muted-foreground mb-3">Prompt versions in use</p>
              <div className="flex flex-wrap gap-2">
                {(stats.prompt_versions || []).map((p) => (
                  <span key={p.version} className="inline-flex items-center gap-1.5 text-xs bg-secondary border border-border px-2.5 py-1 font-mono">
                    {p.version} <span className="text-muted-foreground">×{p.count}</span>
                  </span>
                ))}
                {(stats.prompt_versions || []).length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
              </div>
              <p className="text-xs text-muted-foreground mt-3">{aic.note}</p>
            </div>
          </div>
        )}

        {/* ---------------- AI LOG ---------------- */}
        {tab === "AI Log" && (
          <div data-testid="admin-ai-log">
            {!analyses ? <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> Loading log…</div> : (
              <>
                <p className="text-sm text-muted-foreground mb-4">{analyses.total} analyses total · showing latest {analyses.rows.length}</p>
                <div className="overflow-x-auto border border-border">
                  <table className="w-full text-[13px]" data-testid="ai-log-table">
                    <thead className="bg-secondary text-muted-foreground">
                      <tr className="text-left">
                        {["Date", "User", "Assessment", "Pred", "Actual", "Conf", "Words", "Pg", "Img", "Tbl", "Refs", "Model", "Cost", "Time", ""].map((h) => (
                          <th key={h} className="px-2.5 py-2 font-medium whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analyses.rows.map((r) => (
                        <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`ai-log-row-${r.id}`}>
                          <td className="px-2.5 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                          <td className="px-2.5 py-2 max-w-[140px] truncate" title={r.user}>{r.user}</td>
                          <td className="px-2.5 py-2 whitespace-nowrap">{r.assessment_type || (r.status === "error" ? <span className="text-[#B91C1C]">error</span> : "—")}</td>
                          <td className="px-2.5 py-2 font-mono">{r.predicted_grade ?? "—"}</td>
                          <td className="px-2.5 py-2 font-mono">{r.actual_grade ?? "—"}</td>
                          <td className="px-2.5 py-2 font-mono">{r.confidence != null ? `${r.confidence}%` : "—"}</td>
                          <td className="px-2.5 py-2 font-mono">{r.word_count || "—"}</td>
                          <td className="px-2.5 py-2 font-mono">{r.pages || "—"}</td>
                          <td className="px-2.5 py-2 font-mono">{r.images || "—"}</td>
                          <td className="px-2.5 py-2 font-mono">{r.tables || "—"}</td>
                          <td className="px-2.5 py-2">{r.references_detected ? <Check size={14} className="text-[#15803D]" /> : <X size={14} className="text-[#B91C1C]" />}</td>
                          <td className="px-2.5 py-2 whitespace-nowrap text-[11px] text-muted-foreground">{(r.models_used || [])[0] || "—"}</td>
                          <td className="px-2.5 py-2 font-mono text-[11px]">{r.ai_cost_estimate ? usd(r.ai_cost_estimate) : "—"}</td>
                          <td className="px-2.5 py-2 font-mono">{r.processing_time_s != null ? `${r.processing_time_s}s` : "—"}</td>
                          <td className="px-2.5 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => openDebug(r.id)} data-testid={`debug-btn-${r.id}`} title="Debug"
                                className="inline-flex items-center gap-1 text-oxford hover:underline"><Bug size={13} /></button>
                              <button onClick={() => openMark(r.id)} data-testid={`actual-btn-${r.id}`} title="Enter actual mark"
                                className="inline-flex items-center gap-1 text-muted-foreground hover:text-oxford"><Target size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ---------------- CALIBRATION ---------------- */}
        {tab === "Calibration" && (
          <div data-testid="admin-calibration">
            {!calib ? <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> Loading…</div> : (
              <>
                <div className="grid sm:grid-cols-4 gap-px bg-border border border-border mb-8">
                  <Stat icon={Target} label="Validated" value={calib.metrics.validated} sub="marks entered" testId="cal-validated" />
                  <Stat icon={Target} label="Avg error" value={calib.metrics.average_error != null ? `±${calib.metrics.average_error}%` : "—"} sub="mean abs. error" testId="cal-error" />
                  <Stat icon={Target} label="Within ±5%" value={calib.metrics.within_5 != null ? `${calib.metrics.within_5}%` : "—"} sub="of validated" testId="cal-within5" />
                  <Stat icon={Target} label="Within ±10%" value={calib.metrics.within_10 != null ? `${calib.metrics.within_10}%` : "—"} sub="of validated" testId="cal-within10" />
                </div>
                <div className="overflow-x-auto border border-border">
                  <table className="w-full text-[13px]" data-testid="calibration-table">
                    <thead className="bg-secondary text-muted-foreground">
                      <tr className="text-left">
                        {["Date", "Assignment", "University", "Type", "Predicted", "Actual", "Difference"].map((h) => (
                          <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {calib.rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No validated marks yet. Enter actual marks from the AI Log tab.</td></tr>}
                      {calib.rows.map((r) => (
                        <tr key={r.id} className="border-t border-border">
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                          <td className="px-3 py-2 max-w-[200px] truncate" title={r.filename}>{r.filename}</td>
                          <td className="px-3 py-2">{r.university || "—"}</td>
                          <td className="px-3 py-2">{r.assessment_type || "—"}</td>
                          <td className="px-3 py-2 font-mono">{r.predicted ?? "—"}</td>
                          <td className="px-3 py-2 font-mono">{r.actual ?? "—"}</td>
                          <td className={`px-3 py-2 font-mono ${Math.abs(r.difference || 0) <= 5 ? "text-[#15803D]" : Math.abs(r.difference || 0) <= 10 ? "text-[#B45309]" : "text-[#B91C1C]"}`}>
                            {r.difference != null ? (r.difference > 0 ? `+${r.difference}` : r.difference) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ---------------- CALIBRATION CENTRE ---------------- */}
        {tab === "Calibration Centre" && (
          <div data-testid="admin-calibration-centre">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <p className="overline text-oxford">Phase 2–4</p>
                <p className="text-sm text-muted-foreground">Dedicated calibration workspace — records, dashboard, and layer settings.</p>
              </div>
              <div className="flex gap-1 flex-wrap" data-testid="cal-subtabs">
                {["Dashboard", "Records", "Versions", "Weekly", "Band", "Settings"].map((s) => (
                  <button key={s} onClick={() => setCalSection(s)}
                    data-testid={`cal-subtab-${s.toLowerCase()}`}
                    className={`px-3.5 py-1.5 text-sm border ${calSection === s ? "border-oxford text-oxford bg-secondary" : "border-border text-muted-foreground hover:text-foreground"}`}>{s}</button>
                ))}
              </div>
            </div>

            {/* --- Dashboard --- */}
            {calSection === "Dashboard" && (
              !calDash ? <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> Loading dashboard…</div> : (
                <div data-testid="cal-dashboard">
                  {/* ---------- CALIBRATION HEALTH WIDGET (top-of-page) ---------- */}
                  {calHealth && (
                    <div
                      className={`border p-6 mb-8 ${calHealth.overall_health === "red" ? "border-[#B91C1C] bg-[#B91C1C]/5" : calHealth.overall_health === "amber" ? "border-[#B45309] bg-[#B45309]/5" : "border-[#15803D] bg-[#15803D]/5"}`}
                      data-testid="cal-health-widget">
                      <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center ${calHealth.overall_health === "red" ? "bg-[#B91C1C]" : calHealth.overall_health === "amber" ? "bg-[#B45309]" : "bg-[#15803D]"}`}>
                            <Activity size={26} className="text-white" />
                          </div>
                          <div>
                            <p className="overline text-muted-foreground mb-0.5">Calibration Health</p>
                            <p className="font-serif text-2xl text-oxford uppercase" data-testid="cal-health-status">{calHealth.overall_health}</p>
                          </div>
                        </div>
                        <button onClick={doCheckDrift} disabled={checkingDrift}
                          data-testid="cal-drift-check-btn"
                          className="inline-flex items-center gap-2 text-xs border border-border px-3 py-1.5 hover:border-oxford hover:text-oxford disabled:opacity-60">
                          {checkingDrift ? <Loader2 className="animate-spin" size={12} /> : <Bell size={12} />} Check drift now
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Current MAE</p>
                          <p className="font-mono text-lg">{calHealth.overall_mae != null ? `±${calHealth.overall_mae}%` : "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Signed bias</p>
                          <p className="font-mono text-lg">{calHealth.overall_signed_bias != null ? (calHealth.overall_signed_bias > 0 ? `+${calHealth.overall_signed_bias}` : calHealth.overall_signed_bias) : "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Active version</p>
                          <p className="font-serif text-lg text-oxford">{calHealth.active_version || "None"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Regression</p>
                          <p className="font-mono text-lg uppercase">{calHealth.last_regression_run?.status || "not run"}</p>
                        </div>
                      </div>
                      <div className="flex gap-4 mt-4 pt-3 border-t border-border text-sm flex-wrap" data-testid="cal-health-counts">
                        <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#15803D]" /> {calHealth.counts.green} green</span>
                        <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#B45309]" /> {calHealth.counts.amber} amber</span>
                        <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#B91C1C]" /> {calHealth.counts.red} red</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{calHealth.total_validated} validated records total</span>
                      </div>
                      {/* Bucket status list (only when any bucket is not green) */}
                      {calHealth.buckets.some((b) => b.status !== "green" || (b.reasons && b.reasons.length > 0)) && (
                        <div className="mt-3 space-y-1.5" data-testid="cal-health-buckets">
                          {calHealth.buckets.filter((b) => b.status !== "green").map((b) => (
                            <div key={b.key} className="flex items-start gap-3 text-xs">
                              <span className={`inline-block px-2 py-0.5 uppercase tracking-wide text-white ${b.status === "red" ? "bg-[#B91C1C]" : "bg-[#B45309]"}`}>{b.status}</span>
                              <div>
                                <p className="font-medium">{b.label} <span className="font-mono text-muted-foreground">· n={b.n} · bias {b.signed_bias != null ? (b.signed_bias > 0 ? `+${b.signed_bias}` : b.signed_bias) : "—"} · MAE ±{b.mae ?? "—"}</span></p>
                                <p className="text-muted-foreground">{(b.reasons || []).join(" · ")}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-8">
                    <Stat icon={Target} label="Records" value={calDash.metrics.total_records} sub={`${calDash.metrics.validated_records} validated`} testId="cal-stat-records" />
                    <Stat icon={Target} label="Avg abs error" value={calDash.metrics.average_error != null ? `±${calDash.metrics.average_error}%` : "—"} sub="mean |actual − raw|" testId="cal-stat-avg" />
                    <Stat icon={Target} label="Median error" value={calDash.metrics.median_error != null ? `±${calDash.metrics.median_error}%` : "—"} sub="typical error" testId="cal-stat-median" />
                    <Stat icon={Target} label="Signed bias" value={calDash.metrics.mean_signed_error != null ? `${calDash.metrics.mean_signed_error > 0 ? "+" : ""}${calDash.metrics.mean_signed_error}` : "—"} sub="+ under-predicting" testId="cal-stat-bias" />
                    <Stat icon={Target} label="Within ±5%" value={calDash.metrics.within_5 != null ? `${calDash.metrics.within_5}%` : "—"} sub="of validated" testId="cal-stat-w5" />
                    <Stat icon={Target} label="Within ±10%" value={calDash.metrics.within_10 != null ? `${calDash.metrics.within_10}%` : "—"} sub="of validated" testId="cal-stat-w10" />
                    <Stat icon={Sliders} label="Layer" value={calDash.settings.active ? "ACTIVE" : "OFF"} sub={`min ${calDash.settings.min_samples}/bucket · cap ±${calDash.settings.max_adjustment}`} testId="cal-stat-layer" />
                    <Stat icon={Cpu} label="Prompt" value="v1.0.0" sub="frozen 2026-02" testId="cal-stat-prompt" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-card border border-border p-6" data-testid="cal-over">
                      <p className="overline text-muted-foreground mb-3 flex items-center gap-1.5"><ArrowUp size={14} /> Largest over-predictions</p>
                      {calDash.top_over_predictions.length === 0 && <p className="text-sm text-muted-foreground">No over-predictions yet.</p>}
                      {calDash.top_over_predictions.map((r) => (
                        <div key={r.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                          <span className="text-sm truncate max-w-[60%]" title={r.assignment_title}>{r.assignment_title || "—"}</span>
                          <span className="font-mono text-sm text-[#B91C1C] tabular-nums">{r.myae_raw_prediction} → {r.actual_lecturer_mark} · {r.signed_error > 0 ? "+" : ""}{r.signed_error}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bg-card border border-border p-6" data-testid="cal-under">
                      <p className="overline text-muted-foreground mb-3 flex items-center gap-1.5"><ArrowDown size={14} /> Largest under-predictions</p>
                      {calDash.top_under_predictions.length === 0 && <p className="text-sm text-muted-foreground">No under-predictions yet.</p>}
                      {calDash.top_under_predictions.map((r) => (
                        <div key={r.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                          <span className="text-sm truncate max-w-[60%]" title={r.assignment_title}>{r.assignment_title || "—"}</span>
                          <span className="font-mono text-sm text-[#15803D] tabular-nums">{r.myae_raw_prediction} → {r.actual_lecturer_mark} · +{r.signed_error}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {["subject", "module_level", "assessment_type", "university"].map((field) => (
                    <div key={field} className="bg-card border border-border p-6 mb-6" data-testid={`cal-breakdown-${field}`}>
                      <p className="overline text-muted-foreground mb-3">Accuracy by {field.replace("_", " ")}</p>
                      {calDash.breakdown[field].length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {calDash.breakdown[field].map((b) => (
                            <div key={b.key} className="border border-border p-2.5">
                              <p className="text-sm font-medium truncate" title={b.key}>{b.key}</p>
                              <p className="text-xs text-muted-foreground font-mono">n={b.count} · avg ±{b.avg_error}% · signed {b.mean_signed_error > 0 ? "+" : ""}{b.mean_signed_error}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="bg-card border border-border p-6 mb-6" data-testid="cal-readiness">
                    <p className="overline text-muted-foreground mb-3 flex items-center gap-1.5"><Sliders size={14} /> Bucket readiness (min {calDash.settings.min_samples})</p>
                    {calDash.bucket_readiness.length === 0 ? <p className="text-sm text-muted-foreground">No buckets yet.</p> : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-[13px]">
                          <thead className="bg-secondary text-muted-foreground">
                            <tr className="text-left">{["Subject", "Level", "Type", "Records", "Mean signed error", "Ready"].map((h) => (<th key={h} className="px-3 py-2 font-medium">{h}</th>))}</tr>
                          </thead>
                          <tbody>
                            {calDash.bucket_readiness.map((b, i) => (
                              <tr key={i} className="border-t border-border">
                                <td className="px-3 py-2">{b.subject}</td>
                                <td className="px-3 py-2 font-mono">{b.module_level}</td>
                                <td className="px-3 py-2">{b.assessment_type}</td>
                                <td className="px-3 py-2 font-mono">{b.count}</td>
                                <td className="px-3 py-2 font-mono">{b.mean_signed_error > 0 ? "+" : ""}{b.mean_signed_error}</td>
                                <td className="px-3 py-2">{b.ready ? <Check size={14} className="text-[#15803D]" /> : <X size={14} className="text-muted-foreground" />}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="bg-card border border-border p-6" data-testid="cal-trend">
                    <p className="overline text-muted-foreground mb-3 flex items-center gap-1.5"><TrendingUp size={14} /> Calibration trend</p>
                    {calDash.trend.length === 0 ? <p className="text-sm text-muted-foreground">No trend data yet.</p> : (
                      <div className="flex gap-2 flex-wrap">
                        {calDash.trend.map((t) => (
                          <div key={t.month} className="border border-border p-2 min-w-[110px]">
                            <p className="font-mono text-xs text-muted-foreground">{t.month}</p>
                            <p className="font-mono text-sm">n={t.count}</p>
                            <p className="font-mono text-xs">±{t.avg_error}%</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* --- Active Version + Preview Impact --- */}
                  <div className="bg-card border border-oxford/40 p-6 mt-6" data-testid="cal-active-version">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><GitBranch size={14} /> Active calibration version</p>
                        {calDash.active_version ? (
                          <>
                            <p className="font-serif text-2xl text-oxford">{calDash.active_version.version}</p>
                            <p className="text-sm text-muted-foreground">Activated {fmtDate(calDash.active_version.date)} · {calDash.active_version.records_count} records at snapshot · MAE {calDash.active_version.overall_mae ?? "—"}</p>
                            {calDash.active_version.notes && <p className="text-xs text-muted-foreground mt-2 italic max-w-[560px]">&ldquo;{calDash.active_version.notes}&rdquo;</p>}
                          </>
                        ) : (
                          <>
                            <p className="font-serif text-2xl text-muted-foreground">No active version</p>
                            <p className="text-sm text-muted-foreground">Calibration layer is currently OFF. Preview V1.0 baseline before activating.</p>
                          </>
                        )}
                      </div>
                      <button onClick={() => openPreview(null)} data-testid="cal-open-preview-btn"
                        className="inline-flex items-center gap-2 border border-oxford text-oxford text-sm px-3.5 py-2 hover:bg-oxford hover:text-white transition-colors">
                        <Eye size={15} /> Preview Impact of current settings
                      </button>
                    </div>
                  </div>

                  {/* --- Assessment-Type Decision Support Table --- */}
                  <div className="bg-card border border-border p-6 mt-6" data-testid="cal-atype-stats">
                    <div className="flex items-start justify-between mb-3 gap-4 flex-wrap">
                      <div>
                        <p className="overline text-muted-foreground mb-1 flex items-center gap-1.5"><Target size={14} /> Assessment-type calibration recommendations</p>
                        <p className="text-xs text-muted-foreground">System recommends adjustments — never silently applied. Buckets with fewer than {calDash.settings.min_samples} records are marked &ldquo;collecting evidence&rdquo; and get no suggestion yet.</p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[13px]">
                        <thead className="bg-secondary text-muted-foreground">
                          <tr className="text-left">
                            {["Type", "N", "MAE", "Signed Bias", "Median", "±5%", "±10%", "Confidence", "Current", "Suggested", "Expected MAE"].map((h) => <th key={h} className="px-2.5 py-2 font-medium whitespace-nowrap">{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {calDash.assessment_type_stats.length === 0 && (
                            <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">No assessment types yet.</td></tr>
                          )}
                          {calDash.assessment_type_stats.map((s) => (
                            <tr key={s.key} className="border-t border-border" data-testid={`cal-atype-${s.key}`}>
                              <td className="px-2.5 py-2 font-medium">{s.label}</td>
                              <td className="px-2.5 py-2 font-mono">{s.n || 0}</td>
                              <td className="px-2.5 py-2 font-mono">{s.mae != null ? `±${s.mae}` : "—"}</td>
                              <td className={`px-2.5 py-2 font-mono ${(s.mean_signed_error || 0) > 0 ? "text-[#15803D]" : (s.mean_signed_error || 0) < 0 ? "text-[#B91C1C]" : ""}`}>
                                {s.mean_signed_error != null ? (s.mean_signed_error > 0 ? `+${s.mean_signed_error}` : s.mean_signed_error) : "—"}
                              </td>
                              <td className="px-2.5 py-2 font-mono">{s.median_abs_error != null ? `±${s.median_abs_error}` : "—"}</td>
                              <td className="px-2.5 py-2 font-mono">{s.within_5 != null ? `${s.within_5}%` : "—"}</td>
                              <td className="px-2.5 py-2 font-mono">{s.within_10 != null ? `${s.within_10}%` : "—"}</td>
                              <td className="px-2.5 py-2">
                                <span className={`inline-block px-2 py-0.5 text-[11px] uppercase tracking-wide border ${s.confidence === "High" ? "border-[#15803D] text-[#15803D]" : s.confidence === "Medium" ? "border-[#B45309] text-[#B45309]" : "border-muted-foreground text-muted-foreground"}`}>{s.confidence}</span>
                              </td>
                              <td className="px-2.5 py-2 font-mono">{s.current_adjustment != null ? (s.current_adjustment > 0 ? `+${s.current_adjustment}` : s.current_adjustment) : "—"}</td>
                              <td className="px-2.5 py-2 font-mono">
                                {s.ready && s.suggested_adjustment != null ? (
                                  <span className="text-oxford font-medium">{s.suggested_adjustment > 0 ? `+${s.suggested_adjustment}` : s.suggested_adjustment}</span>
                                ) : (
                                  <span className="text-xs text-muted-foreground italic">collecting…</span>
                                )}
                              </td>
                              <td className="px-2.5 py-2 font-mono">
                                {s.expected_improvement ? (
                                  <span className={s.expected_improvement.mae_delta < 0 ? "text-[#15803D]" : "text-[#B91C1C]"}>
                                    ±{s.expected_improvement.projected_mae} ({s.expected_improvement.mae_delta > 0 ? "+" : ""}{s.expected_improvement.mae_delta})
                                  </span>
                                ) : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                      <button onClick={() => {
                        // seed preview with SUGGESTED where ready, else current
                        const draft = { ...(calSettings?.assessment_type_adjustments || {}) };
                        (calDash.assessment_type_stats || []).forEach((s) => {
                          if (s.ready && s.suggested_adjustment != null) draft[s.key] = s.suggested_adjustment;
                        });
                        openPreview(draft);
                      }} data-testid="cal-apply-suggested-btn"
                        className="inline-flex items-center gap-2 bg-oxford text-white text-sm px-3.5 py-2 hover:bg-[#2C5282] transition-colors">
                        <PlayCircle size={15} /> Preview all suggested adjustments
                      </button>
                      <span className="text-xs text-muted-foreground">Preview builds a projected MAE / bias before you save any version.</span>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* --- Versions --- */}
            {calSection === "Versions" && (
              <div data-testid="cal-versions">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-medium">Calibration version history</p>
                    <p className="text-xs text-muted-foreground">Every change is versioned. Nothing is overwritten — one click to rollback any previous version.</p>
                  </div>
                  <button onClick={() => openPreview(null)} data-testid="cal-new-version-preview-btn"
                    className="inline-flex items-center gap-2 border border-oxford text-oxford text-sm px-3.5 py-2 hover:bg-oxford hover:text-white transition-colors">
                    <Eye size={15} /> Preview & save new version
                  </button>
                </div>
                {!calVersions ? <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> Loading versions…</div> : (
                  <div className="overflow-x-auto border border-border">
                    <table className="w-full text-[13px]" data-testid="cal-versions-table">
                      <thead className="bg-secondary text-muted-foreground">
                        <tr className="text-left">
                          {["Version", "Date", "Status", "Records", "MAE", "Signed bias", "Adjustments", "Notes", ""].map((h) => <th key={h} className="px-2.5 py-2 font-medium whitespace-nowrap">{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {calVersions.rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">No versions yet.</td></tr>}
                        {calVersions.rows.map((v) => (
                          <tr key={v.version} className="border-t border-border" data-testid={`cal-version-${v.version}`}>
                            <td className="px-2.5 py-2 font-serif text-oxford">{v.version}</td>
                            <td className="px-2.5 py-2 font-mono">{fmtDate(v.date)}</td>
                            <td className="px-2.5 py-2">
                              {v.active ? (
                                <span className="inline-block px-2 py-0.5 text-[11px] uppercase tracking-wide bg-oxford text-white">Active</span>
                              ) : (
                                <span className="inline-block px-2 py-0.5 text-[11px] uppercase tracking-wide border border-border text-muted-foreground">Inactive</span>
                              )}
                            </td>
                            <td className="px-2.5 py-2 font-mono">{v.records_count}</td>
                            <td className="px-2.5 py-2 font-mono">{v.overall_mae != null ? `±${v.overall_mae}` : "—"}</td>
                            <td className="px-2.5 py-2 font-mono">{v.signed_bias != null ? (v.signed_bias > 0 ? `+${v.signed_bias}` : v.signed_bias) : "—"}</td>
                            <td className="px-2.5 py-2 max-w-[260px] text-xs">
                              {Object.keys(v.assessment_type_adjustments || {}).length === 0 ? <span className="text-muted-foreground">—</span> : (
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(v.assessment_type_adjustments).map(([k, val]) => (
                                    <span key={k} className="font-mono border border-border px-1.5 py-0.5">{k}:{val > 0 ? `+${val}` : val}</span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-2.5 py-2 max-w-[280px] text-xs italic text-muted-foreground truncate" title={v.notes}>{v.notes || "—"}</td>
                            <td className="px-2.5 py-2 whitespace-nowrap">
                              {!v.active && (
                                <button onClick={() => activateVersion(v.version)}
                                  data-testid={`cal-activate-${v.version}`}
                                  className="inline-flex items-center gap-1 text-xs border border-oxford text-oxford px-2 py-1 hover:bg-oxford hover:text-white transition-colors">
                                  <History size={12} /> Activate
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* --- Weekly Cycle --- */}
            {calSection === "Weekly" && (
              <div data-testid="cal-weekly">
                {!calWeekly ? <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> Loading weekly summary…</div> : (
                  <div className="space-y-6">
                    {/* --- Monday Ops Report card --- */}
                    <div className="bg-card border border-oxford/40 p-6" data-testid="cal-ops-report">
                      <div className="flex items-start justify-between flex-wrap gap-4">
                        <div className="flex-1 min-w-[280px]">
                          <p className="overline text-muted-foreground mb-1 flex items-center gap-1.5"><Mail size={14} /> Monday MyAE Operations Report</p>
                          <p className="text-sm">Comprehensive weekly digest — Platform Health, Validation Summary, Calibration, System Health, Business Metrics, Release Readiness.</p>
                          <p className="text-xs text-muted-foreground mt-1">Auto-sent every <b>Monday 08:00 UTC</b> to admin + configured recipients. Add extra recipients in Settings.</p>
                          {lastOpsSend && (
                            <p className="text-xs text-muted-foreground mt-2 font-mono">
                              Last sent: {fmtDate(lastOpsSend.timestamp)} · {lastOpsSend.trigger} · {lastOpsSend.recipients?.length || 0} recipient(s)
                            </p>
                          )}
                        </div>
                        <button onClick={doSendOpsReport} disabled={sendingOpsReport}
                          data-testid="cal-send-ops-report-btn"
                          className="inline-flex items-center gap-2 bg-oxford text-white text-sm px-3.5 py-2 hover:bg-[#2C5282] disabled:opacity-60">
                          {sendingOpsReport ? <><Loader2 className="animate-spin" size={15} /> Sending…</> : <><Mail size={15} /> Send Weekly Report Now</>}
                        </button>
                      </div>
                      {/* --- TL;DR preview --- */}
                      {opsReport?.tldr && (
                        <div className="mt-4 pt-4 border-t border-border" data-testid="cal-tldr">
                          <p className="font-mono text-sm font-semibold text-oxford break-words" data-testid="cal-tldr-headline">{opsReport.tldr.headline}</p>
                          <p className="text-sm italic text-muted-foreground mt-1">{opsReport.tldr.narrative}</p>
                        </div>
                      )}
                      {/* --- Since Last Week --- */}
                      {opsReport?.since_last_week?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border" data-testid="cal-since-last-week">
                          <p className="overline text-muted-foreground mb-2">Since Last Week</p>
                          <ul className="space-y-1 text-sm">
                            {opsReport.since_last_week.map((e, i) => (
                              <li key={i} className={e.good ? "text-[#15803D]" : "text-[#B91C1C]"}>{e.text}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {opsReport && opsReport.since_last_week?.length === 0 && (
                        <p className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground italic" data-testid="cal-since-last-week-empty">First archived report — Since Last Week comparisons will appear next week.</p>
                      )}
                    </div>

                    <div className="bg-card border border-border p-6">
                      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                        <div>
                          <p className="overline text-muted-foreground mb-1 flex items-center gap-1.5"><CalendarClock size={14} /> Weekly Cycle · week ending {calWeekly.week_ending}</p>
                          <p className="text-xs text-muted-foreground">Structured review flow: new records → accuracy → recommendations → regression gate → release.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border">
                        <Stat icon={Plus} label="New this week" value={calWeekly.new_validated_this_week} sub="validated records" testId="cal-weekly-new" />
                        <Stat icon={Target} label="Current MAE" value={calWeekly.current_mae != null ? `±${calWeekly.current_mae}%` : "—"} sub={`${calWeekly.total_validated} total`} testId="cal-weekly-mae" />
                        <Stat icon={ArrowUp} label="Signed bias" value={calWeekly.current_signed_bias != null ? (calWeekly.current_signed_bias > 0 ? `+${calWeekly.current_signed_bias}` : calWeekly.current_signed_bias) : "—"} sub="+ under-predicting" testId="cal-weekly-bias" />
                        <Stat icon={GitBranch} label="Recommendations" value={calWeekly.recommendations.length} sub="pending review" testId="cal-weekly-recs" />
                      </div>
                    </div>

                    {/* Recommendations */}
                    <div className="bg-card border border-border p-6" data-testid="cal-weekly-recs-panel">
                      <p className="overline text-muted-foreground mb-3">Recommended calibration changes</p>
                      {calWeekly.recommendations.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No material recommendations this week. Continue collecting evidence.</p>
                      ) : (
                        <div className="space-y-2">
                          {calWeekly.recommendations.map((r) => (
                            <div key={r.assessment_type} className="flex items-center justify-between border border-border p-3">
                              <div>
                                <p className="text-sm font-medium">{r.label}</p>
                                <p className="text-xs text-muted-foreground font-mono">n={r.n} · current {r.current_adjustment > 0 ? `+${r.current_adjustment}` : r.current_adjustment} → suggested {r.suggested_adjustment > 0 ? `+${r.suggested_adjustment}` : r.suggested_adjustment} (Δ {r.delta > 0 ? `+${r.delta}` : r.delta})</p>
                              </div>
                            </div>
                          ))}
                          <button onClick={() => {
                            const draft = { ...(calSettings?.assessment_type_adjustments || {}) };
                            calWeekly.recommendations.forEach((r) => { draft[r.assessment_type] = r.suggested_adjustment; });
                            openPreview(draft);
                          }} data-testid="cal-weekly-preview-btn"
                            className="mt-3 inline-flex items-center gap-2 border border-oxford text-oxford text-sm px-3.5 py-2 hover:bg-oxford hover:text-white transition-colors">
                            <Eye size={15} /> Preview these recommendations
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Regression Gate */}
                    <div className="bg-card border border-border p-6" data-testid="cal-weekly-regression">
                      <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
                        <div>
                          <p className="overline text-muted-foreground mb-1 flex items-center gap-1.5"><PlayCircle size={14} /> Release regression gate</p>
                          <p className="text-xs text-muted-foreground">Final gate before Production deploy. Runs the full reference & citation regression suite.</p>
                        </div>
                        <button onClick={doRunRegression} disabled={runningRegression}
                          data-testid="cal-run-regression-btn"
                          className="inline-flex items-center gap-2 bg-oxford text-white text-sm px-3.5 py-2 hover:bg-[#2C5282] transition-colors disabled:opacity-60">
                          {runningRegression ? <><Loader2 className="animate-spin" size={15} /> Running…</> : <><PlayCircle size={15} /> Run full regression</>}
                        </button>
                      </div>
                      {calRegression ? (
                        <div className={`border p-3 ${calRegression.status === "pass" ? "border-[#15803D] bg-[#15803D]/5" : "border-[#B91C1C] bg-[#B91C1C]/5"}`}>
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span className={`inline-block px-2 py-0.5 text-[11px] uppercase tracking-wide ${calRegression.status === "pass" ? "bg-[#15803D] text-white" : "bg-[#B91C1C] text-white"}`}>{calRegression.status}</span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {calRegression.tests_total != null ? `${calRegression.tests_passed}/${calRegression.tests_total} tests` : "no test count"}
                              {" · "}
                              {calRegression.runtime_s}s
                              {" · "}
                              {fmtDate(calRegression.timestamp)}
                            </span>
                          </div>
                          {calRegression.failed_tests?.length > 0 && (
                            <div className="text-xs font-mono">
                              <p className="text-[#B91C1C] mb-1">Failed:</p>
                              {calRegression.failed_tests.map((t, i) => <p key={i} className="truncate">• {t}</p>)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No regression run recorded yet.</p>
                      )}
                    </div>

                    {/* Recent validated records */}
                    <div className="bg-card border border-border p-6" data-testid="cal-weekly-recent">
                      <p className="overline text-muted-foreground mb-3">New validated records this week</p>
                      {calWeekly.recent_records.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No new records this week.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-[13px]">
                            <thead className="bg-secondary text-muted-foreground">
                              <tr className="text-left">{["Date", "Assignment", "Type", "Raw → Actual", "Δ"].map((h) => <th key={h} className="px-2.5 py-2 font-medium">{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {calWeekly.recent_records.map((r, i) => (
                                <tr key={i} className="border-t border-border">
                                  <td className="px-2.5 py-2">{fmtDate(r.created_at)}</td>
                                  <td className="px-2.5 py-2 max-w-[240px] truncate" title={r.assignment_title}>{r.assignment_title || "—"}</td>
                                  <td className="px-2.5 py-2">{r.assessment_type || "—"}</td>
                                  <td className="px-2.5 py-2 font-mono">{r.myae_raw_prediction ?? "—"} → {r.actual_lecturer_mark ?? "—"}</td>
                                  <td className={`px-2.5 py-2 font-mono ${Math.abs(r.signed_error || 0) <= 5 ? "text-[#15803D]" : Math.abs(r.signed_error || 0) <= 10 ? "text-[#B45309]" : "text-[#B91C1C]"}`}>
                                    {r.signed_error != null ? (r.signed_error > 0 ? `+${r.signed_error}` : r.signed_error) : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* --- Records --- */}
            {calSection === "Records" && (
              <div data-testid="cal-records">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-muted-foreground">{calRecords?.total ?? 0} calibration records</p>
                  <button onClick={() => setShowManualEntry(true)} data-testid="cal-add-manual-btn"
                    className="inline-flex items-center gap-2 bg-oxford text-white text-sm px-3.5 py-2 hover:bg-[#2C5282] transition-colors">
                    <Plus size={15} /> Add manual record
                  </button>
                </div>
                {!calRecords ? <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> Loading records…</div> : (
                  <div className="overflow-x-auto border border-border">
                    <table className="w-full text-[13px]" data-testid="cal-records-table">
                      <thead className="bg-secondary text-muted-foreground">
                        <tr className="text-left">{["Date", "Assignment", "Subject", "Level", "Type", "University", "Raw", "Actual", "Δ", "Prompt", ""].map((h) => (<th key={h} className="px-2.5 py-2 font-medium whitespace-nowrap">{h}</th>))}</tr>
                      </thead>
                      <tbody>
                        {calRecords.rows.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">No calibration records yet. Add lecturer marks from the AI Log tab, or use the button above.</td></tr>}
                        {calRecords.rows.map((r) => (
                          <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`cal-row-${r.id}`}>
                            <td className="px-2.5 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                            <td className="px-2.5 py-2 max-w-[200px] truncate" title={r.assignment_title}>{r.assignment_title || "—"}</td>
                            <td className="px-2.5 py-2">{r.subject || "—"}</td>
                            <td className="px-2.5 py-2 font-mono">{r.module_level || "—"}</td>
                            <td className="px-2.5 py-2">{r.assessment_type || "—"}</td>
                            <td className="px-2.5 py-2 max-w-[140px] truncate" title={r.university}>{r.university || "—"}</td>
                            <td className="px-2.5 py-2 font-mono">{r.myae_raw_prediction ?? "—"}</td>
                            <td className="px-2.5 py-2 font-mono">{r.actual_lecturer_mark ?? "—"}</td>
                            <td className={`px-2.5 py-2 font-mono ${Math.abs(r.signed_error || 0) <= 5 ? "text-[#15803D]" : Math.abs(r.signed_error || 0) <= 10 ? "text-[#B45309]" : "text-[#B91C1C]"}`}>
                              {r.signed_error != null ? (r.signed_error > 0 ? `+${r.signed_error}` : r.signed_error) : "—"}
                            </td>
                            <td className="px-2.5 py-2 text-[11px] text-muted-foreground font-mono">{r.prompt_version || "—"}</td>
                            <td className="px-2.5 py-2 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <button onClick={() => setEditingRecord({ ...r })} data-testid={`cal-edit-${r.id}`} title="Edit"
                                  className="text-muted-foreground hover:text-oxford"><PenSquare size={13} /></button>
                                <button onClick={() => removeRecord(r.id)} data-testid={`cal-delete-${r.id}`} title="Delete"
                                  className="text-muted-foreground hover:text-[#B91C1C]"><Trash2 size={13} /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* --- Band (UK undergraduate band calibration) --- */}
            {calSection === "Band" && (
              <div data-testid="cal-band">
                <BandCalibrationPanel />
              </div>
            )}

            {/* --- Settings --- */}
            {calSection === "Settings" && (
              <div data-testid="cal-settings" className="max-w-2xl">
                {!calSettings ? <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> Loading…</div> : (
                  <div className="bg-card border border-border p-6 space-y-6">
                    <div>
                      <p className="text-sm font-medium mb-1">Calibration layer</p>
                      <p className="text-xs text-muted-foreground mb-3">When ON, each new analysis stores both the raw AI grade and a calibrated grade adjusted from validated lecturer marks. Students see the calibrated grade; the raw is admin-only.</p>
                      <label className="inline-flex items-center gap-3 cursor-pointer">
                        <button onClick={() => saveCalSettings({ active: !calSettings.active })} disabled={savingSettings}
                          data-testid="cal-active-toggle"
                          className={`relative w-12 h-6 rounded-full transition-colors ${calSettings.active ? "bg-oxford" : "bg-muted-foreground/30"}`}>
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${calSettings.active ? "translate-x-6" : ""}`} />
                        </button>
                        <span className="text-sm font-medium">{calSettings.active ? "Layer ACTIVE" : "Layer OFF (raw = final)"}</span>
                      </label>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-1">Readiness threshold</p>
                      <p className="text-xs text-muted-foreground mb-3">Minimum number of validated calibration records required in a bucket before its adjustment becomes active. Broader fallback buckets are used when a specific bucket is short.</p>
                      <div className="flex items-center gap-3">
                        <input type="number" min="1" max="500" defaultValue={calSettings.min_samples} data-testid="cal-min-samples"
                          onBlur={(e) => { const n = parseInt(e.target.value, 10); if (!isNaN(n) && n !== calSettings.min_samples) saveCalSettings({ min_samples: n }); }}
                          className="w-24 border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                        <span className="text-xs text-muted-foreground">records / bucket</span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-1">Maximum adjustment</p>
                      <p className="text-xs text-muted-foreground mb-3">Absolute cap on how many percentage points calibration can shift a raw grade in either direction (safety guard).</p>
                      <div className="flex items-center gap-3">
                        <input type="number" min="0" max="50" step="0.5" defaultValue={calSettings.max_adjustment} data-testid="cal-max-adj"
                          onBlur={(e) => { const n = parseFloat(e.target.value); if (!isNaN(n) && n !== calSettings.max_adjustment) saveCalSettings({ max_adjustment: n }); }}
                          className="w-24 border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                        <span className="text-xs text-muted-foreground">± percentage points</span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-1">Assessment-type adjustments <span className="text-xs text-muted-foreground">(active version)</span></p>
                      <p className="text-xs text-muted-foreground mb-3">Percentage-point adjustment applied to the raw AI grade when the data-driven bucket lacks {calSettings.min_samples} records. Editing here is a live edit — to preserve history use Versions → Preview & save new version.</p>
                      <div className="space-y-2" data-testid="cal-atype-editor">
                        {Object.entries(calSettings.assessment_type_adjustments || {}).map(([k, v]) => (
                          <div key={k} className="flex items-center gap-3">
                            <span className="w-40 text-sm capitalize">{k.replace(/_/g, " ")}</span>
                            <input type="number" step="0.5" defaultValue={v}
                              data-testid={`cal-atype-input-${k}`}
                              onBlur={(e) => {
                                const n = parseFloat(e.target.value);
                                if (!isNaN(n) && n !== v) {
                                  const next = { ...(calSettings.assessment_type_adjustments || {}), [k]: n };
                                  saveCalSettings({ assessment_type_adjustments: next });
                                }
                              }}
                              className="w-24 border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                            <span className="text-xs text-muted-foreground">pp</span>
                            <button onClick={() => {
                              if (!window.confirm(`Remove '${k}' adjustment?`)) return;
                              const next = { ...(calSettings.assessment_type_adjustments || {}) };
                              delete next[k];
                              saveCalSettings({ assessment_type_adjustments: next });
                            }} title="Remove" data-testid={`cal-atype-remove-${k}`}
                              className="text-muted-foreground hover:text-[#B91C1C]"><X size={13} /></button>
                          </div>
                        ))}
                        <button onClick={() => {
                          const key = window.prompt("Assessment type key (e.g. portfolio, dissertation):")?.trim().toLowerCase().replace(/[\s-]+/g, "_");
                          if (!key) return;
                          const val = parseFloat(window.prompt("Adjustment in percentage points (e.g. 3):"));
                          if (isNaN(val)) { toast.error("Invalid number."); return; }
                          const next = { ...(calSettings.assessment_type_adjustments || {}), [key]: val };
                          saveCalSettings({ assessment_type_adjustments: next });
                        }} data-testid="cal-atype-add"
                          className="inline-flex items-center gap-2 border border-border text-sm px-3 py-1.5 hover:border-oxford hover:text-oxford transition-colors">
                          <Plus size={13} /> Add assessment-type
                        </button>
                      </div>
                    </div>

                    <div className="border-t border-border pt-4">
                      <p className="text-sm font-medium mb-1">Drift monitoring thresholds</p>
                      <p className="text-xs text-muted-foreground mb-3">RED alert triggers at the values below; AMBER fires at 70% of each threshold. Alerts NEVER modify calibration automatically — they only recommend a review.</p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Bias threshold (±pp)</p>
                          <input type="number" min="1" max="50" step="0.5" defaultValue={calSettings.drift_bias_threshold}
                            data-testid="cal-drift-bias" onBlur={(e) => { const n = parseFloat(e.target.value); if (!isNaN(n) && n !== calSettings.drift_bias_threshold) saveCalSettings({ drift_bias_threshold: n }); }}
                            className="w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">MAE threshold (±pp)</p>
                          <input type="number" min="1" max="50" step="0.5" defaultValue={calSettings.drift_mae_threshold}
                            data-testid="cal-drift-mae" onBlur={(e) => { const n = parseFloat(e.target.value); if (!isNaN(n) && n !== calSettings.drift_mae_threshold) saveCalSettings({ drift_mae_threshold: n }); }}
                            className="w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Consecutive drift count</p>
                          <input type="number" min="2" max="20" step="1" defaultValue={calSettings.drift_consecutive_count}
                            data-testid="cal-drift-consecutive" onBlur={(e) => { const n = parseInt(e.target.value, 10); if (!isNaN(n) && n !== calSettings.drift_consecutive_count) saveCalSettings({ drift_consecutive_count: n }); }}
                            className="w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Deterioration (Δ MAE pp)</p>
                          <input type="number" min="1" max="50" step="0.5" defaultValue={calSettings.drift_deterioration_threshold}
                            data-testid="cal-drift-deterioration" onBlur={(e) => { const n = parseFloat(e.target.value); if (!isNaN(n) && n !== calSettings.drift_deterioration_threshold) saveCalSettings({ drift_deterioration_threshold: n }); }}
                            className="w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-1">Alert recipients <span className="text-xs text-muted-foreground">(admin always included)</span></p>
                      <p className="text-xs text-muted-foreground mb-3">Comma-separated email addresses to receive RED drift alerts + Monday Ops Report. Admin email is automatically added at send time.</p>
                      <input type="text" defaultValue={(calSettings.alert_recipients || []).join(", ")}
                        data-testid="cal-alert-recipients"
                        placeholder="e.g. ops@example.com, team@example.com"
                        onBlur={(e) => {
                          const list = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                          const cur = (calSettings.alert_recipients || []).join(", ").split(",").map(s => s.trim()).filter(Boolean);
                          if (JSON.stringify(list) !== JSON.stringify(cur)) saveCalSettings({ alert_recipients: list });
                        }}
                        className="w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
                      {(calSettings.alert_recipients || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(calSettings.alert_recipients || []).map((e) => (
                            <span key={e} className="font-mono text-xs border border-border px-2 py-0.5">{e}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="border-t border-border pt-4 text-xs text-muted-foreground">
                      <p><strong>Frozen prompt:</strong> v1.0.0-frozen-2026-02. Any change to the grading prompt must bump this version so calibration data stays attributed to the exact prompt behaviour that produced it.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ---------------- PRICING & PLANS ---------------- */}
        {tab === "Pricing" && (
          <div data-testid="admin-pricing">
            <PricingPlansPanel />
          </div>
        )}

        {/* ---------------- PARTNERS ---------------- */}
        {tab === "Partners" && (
          <div data-testid="admin-partners">
            <PartnersPanel />
          </div>
        )}

        {/* ---------------- COMMISSIONS ---------------- */}
        {tab === "Commissions" && (
          <div data-testid="admin-commissions">
            <CommissionsPanel />
          </div>
        )}

        {/* ---------------- REWARDS ---------------- */}
        {tab === "Rewards" && (
          <div data-testid="admin-rewards">
            {/* Dashboard */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-6" data-testid="rewards-dashboard">
              <Stat icon={Ticket} label="Entries this month" value={rewardsDash?.entries_this_month ?? "—"} sub={rewardsDash?.month_key || ""} testId="rewards-entries-month" />
              <Stat icon={Users} label="Contributors this month" value={rewardsDash?.contributors_this_month ?? "—"} sub="unique students" testId="rewards-contributors-month" />
              <Stat icon={Trophy} label="Total entries (all time)" value={rewardsDash?.total_entries_all_time ?? "—"} sub="" testId="rewards-entries-total" />
              <Stat icon={Award} label="Total contributors" value={rewardsDash?.total_contributors_all_time ?? "—"} sub="all time" testId="rewards-contributors-total" />
            </div>

            {/* Configurable rewards */}
            {rewardsConfig && (
              <div className="bg-card border border-border p-6 mb-6" data-testid="rewards-config">
                <p className="overline text-muted-foreground mb-1 flex items-center gap-1.5"><Sliders size={14} /> Rewards configuration</p>
                <p className="text-xs text-muted-foreground mb-4">Change point values and exchange rates without a redeploy. Applies immediately to all new contributions.</p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {[
                    ["points_grade_confirm", "Grade confirmation"],
                    ["points_feedback", "Feedback"],
                    ["points_referral", "Verified referral"],
                    ["points_profile_complete", "Profile completion"],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <p className="text-xs text-muted-foreground mb-1">{label}</p>
                      <input type="number" min="0" defaultValue={rewardsConfig[key]}
                        data-testid={`rewards-${key}`}
                        onBlur={(e) => { const n = parseInt(e.target.value, 10); if (!isNaN(n) && n !== rewardsConfig[key]) saveRewardsField({ [key]: n }); }}
                        className="w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                    </div>
                  ))}
                </div>

                <p className="text-sm font-medium mb-2 flex items-center gap-2"><Gift size={14} className="text-oxford" /> Exchange rates</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="border border-border p-3">
                    <p className="text-xs text-muted-foreground mb-2">Analysis credits</p>
                    <div className="flex items-center gap-2 text-sm">
                      <input type="number" min="1" defaultValue={rewardsConfig.exchange_credits_amount} data-testid="rewards-exchange-credits-amount"
                        onBlur={(e) => { const n = parseInt(e.target.value, 10); if (!isNaN(n) && n !== rewardsConfig.exchange_credits_amount) saveRewardsField({ exchange_credits_amount: n }); }}
                        className="w-20 border border-border px-2 py-1.5 bg-background font-mono" />
                      <span className="text-muted-foreground">credits for</span>
                      <input type="number" min="1" defaultValue={rewardsConfig.exchange_credits_cost} data-testid="rewards-exchange-credits-cost"
                        onBlur={(e) => { const n = parseInt(e.target.value, 10); if (!isNaN(n) && n !== rewardsConfig.exchange_credits_cost) saveRewardsField({ exchange_credits_cost: n }); }}
                        className="w-20 border border-border px-2 py-1.5 bg-background font-mono" />
                      <span className="text-muted-foreground">points</span>
                    </div>
                  </div>
                  <div className="border border-border p-3">
                    <p className="text-xs text-muted-foreground mb-2">Pro subscription days</p>
                    <div className="flex items-center gap-2 text-sm">
                      <input type="number" min="1" defaultValue={rewardsConfig.exchange_pro_days_amount} data-testid="rewards-exchange-pro-amount"
                        onBlur={(e) => { const n = parseInt(e.target.value, 10); if (!isNaN(n) && n !== rewardsConfig.exchange_pro_days_amount) saveRewardsField({ exchange_pro_days_amount: n }); }}
                        className="w-20 border border-border px-2 py-1.5 bg-background font-mono" />
                      <span className="text-muted-foreground">Pro days for</span>
                      <input type="number" min="1" defaultValue={rewardsConfig.exchange_pro_days_cost} data-testid="rewards-exchange-pro-cost"
                        onBlur={(e) => { const n = parseInt(e.target.value, 10); if (!isNaN(n) && n !== rewardsConfig.exchange_pro_days_cost) saveRewardsField({ exchange_pro_days_cost: n }); }}
                        className="w-20 border border-border px-2 py-1.5 bg-background font-mono" />
                      <span className="text-muted-foreground">points</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Monthly draw */}
            <div className="bg-card border border-border p-6 mb-6" data-testid="rewards-draw">
              <p className="overline text-muted-foreground mb-1 flex items-center gap-1.5"><Trophy size={14} /> Monthly prize draw · {rewardsDash?.month_key || "—"}</p>
              <p className="text-xs text-muted-foreground mb-4">Each verified contribution counts as one entry — a student with 5 contributions has 5 entries. Winner is picked uniformly at random from the flat entry list.</p>

              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <input value={drawPrize} onChange={(e) => setDrawPrize(e.target.value)}
                  placeholder="Prize (e.g. Amazon £25 voucher)" data-testid="rewards-draw-prize"
                  className="border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
                <input value={drawNotes} onChange={(e) => setDrawNotes(e.target.value)}
                  placeholder="Notes (optional)" data-testid="rewards-draw-notes"
                  className="border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
              </div>
              <button onClick={doPrizeDraw} disabled={drawing || !drawPrize.trim() || !(rewardsDash?.entries_this_month)}
                data-testid="rewards-draw-btn"
                className="inline-flex items-center gap-2 bg-oxford text-white text-sm px-4 py-2 hover:bg-[#2C5282] disabled:opacity-60">
                {drawing ? <><Loader2 className="animate-spin" size={14} /> Drawing…</> : <><Trophy size={14} /> Draw winner</>}
              </button>

              {/* Top contributors */}
              {rewardsDash?.top_contributors_this_month?.length > 0 && (
                <div className="mt-6 pt-4 border-t border-border">
                  <p className="text-xs font-medium mb-2">Top contributors this month</p>
                  <ul className="space-y-1 text-sm">
                    {rewardsDash.top_contributors_this_month.map((u) => (
                      <li key={u.user_id} className="flex items-center justify-between border border-border px-3 py-1.5" data-testid={`rewards-top-${u.user_id}`}>
                        <span className="font-mono text-xs truncate max-w-[70%]">{u.email || u.user_id}</span>
                        <span className="font-mono text-xs text-muted-foreground">{u.entries} entries</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Prize history */}
            <div className="bg-card border border-border p-6" data-testid="rewards-history">
              <p className="overline text-muted-foreground mb-3 flex items-center gap-1.5"><History size={14} /> Prize history</p>
              {!prizeHistory ? <div className="flex items-center gap-2 text-muted-foreground py-4"><Loader2 className="animate-spin" size={16} /> Loading…</div> :
                prizeHistory.rows.length === 0 ? <p className="text-sm text-muted-foreground">No draws yet.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead className="bg-secondary text-muted-foreground">
                      <tr className="text-left">{["Month", "Date", "Winner", "Prize", "Entries", "By"].map((h) => <th key={h} className="px-2.5 py-2 font-medium">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {prizeHistory.rows.map((r) => (
                        <tr key={r.id} className="border-t border-border" data-testid={`rewards-history-${r.id}`}>
                          <td className="px-2.5 py-2 font-mono">{r.month_key}</td>
                          <td className="px-2.5 py-2 font-mono">{fmtDate(r.drawn_at)}</td>
                          <td className="px-2.5 py-2 font-mono truncate max-w-[220px]" title={r.winner_email}>{r.winner_email || r.winner_user_id}</td>
                          <td className="px-2.5 py-2">{r.prize || "—"}</td>
                          <td className="px-2.5 py-2 font-mono">{r.total_entries_at_draw ?? "—"}</td>
                          <td className="px-2.5 py-2 text-muted-foreground text-xs truncate max-w-[180px]" title={r.drawn_by}>{r.drawn_by}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---------------- PROMO ---------------- */}
        {tab === "Promo" && (
          <div data-testid="admin-promo">
            {/* Launch mode — Free until further notice */}
            <div className="bg-gradient-to-br from-emerald-50 via-white to-emerald-50 border-2 border-emerald-300 p-6 mb-6" data-testid="launch-mode-card">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white shrink-0">
                  <Gift size={16} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Launch mode — Free until further notice</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    While this is on, the pricing page shows every price with a strikethrough and a big <span className="font-mono font-bold text-emerald-700">FREE</span> label. Checkout is blocked and users are told everything is free. Turn off to enable paid checkout.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="launch-mode-toggle-wrap">
                  <input
                    type="checkbox"
                    checked={!!launchMode?.free_until_further_notice}
                    disabled={launchSaving || !launchMode}
                    onChange={async (e) => {
                      const on = e.target.checked;
                      setLaunchSaving(true);
                      try {
                        const fresh = await updatePromoMode({ free_until_further_notice: on });
                        setLaunchMode(fresh);
                        toast.success(on ? "Public launch promo enabled — all plans are FREE." : "Public launch promo disabled — paid checkout is live.");
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Could not update launch mode.");
                      } finally { setLaunchSaving(false); }
                    }}
                    data-testid="launch-mode-toggle"
                    className="sr-only peer"
                  />
                  <span className="w-11 h-6 bg-zinc-300 peer-checked:bg-emerald-500 rounded-full relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></span>
                  <span className="text-xs font-medium text-foreground">{launchMode?.free_until_further_notice ? "ON" : "OFF"}</span>
                </label>
              </div>
              {launchMode && (
                <div className="grid sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-emerald-200">
                  <label className="text-xs">
                    <span className="block text-muted-foreground font-medium mb-1">Banner badge text (≤120 chars)</span>
                    <input
                      type="text"
                      value={launchMode.promo_banner_text || ""}
                      onChange={(e) => setLaunchMode({ ...launchMode, promo_banner_text: e.target.value })}
                      maxLength={120}
                      data-testid="launch-banner-text"
                      className="w-full border border-border px-2 py-1.5 text-xs font-mono"
                    />
                  </label>
                  <label className="text-xs">
                    <span className="block text-muted-foreground font-medium mb-1">Subline (≤240 chars)</span>
                    <input
                      type="text"
                      value={launchMode.promo_subline || ""}
                      onChange={(e) => setLaunchMode({ ...launchMode, promo_subline: e.target.value })}
                      maxLength={240}
                      data-testid="launch-banner-subline"
                      className="w-full border border-border px-2 py-1.5 text-xs"
                    />
                  </label>
                  <div className="sm:col-span-2 flex justify-end">
                    <button
                      onClick={async () => {
                        setLaunchSaving(true);
                        try {
                          const fresh = await updatePromoMode({
                            promo_banner_text: launchMode.promo_banner_text,
                            promo_subline: launchMode.promo_subline,
                          });
                          setLaunchMode(fresh);
                          toast.success("Banner text saved.");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Could not save.");
                        } finally { setLaunchSaving(false); }
                      }}
                      disabled={launchSaving}
                      data-testid="launch-save-banner-btn"
                      className="bg-emerald-600 text-white px-3 py-1.5 text-xs hover:bg-emerald-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {launchSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save banner
                    </button>
                  </div>
                </div>
              )}

              {/* Fair-use ceiling (abuse / cost protection — NOT a free allowance) */}
              {launchMode && (
                <div className="mt-4 pt-3 border-t border-emerald-200">
                  <p className="text-xs font-semibold text-foreground">Fair-use ceiling</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 mb-3 leading-relaxed">
                    Abuse / cost-protection caps while the launch promo is ON. These are NOT presented as the end of the user&apos;s free entitlement — the UI shows a &quot;temporary limit, try again later&quot; message.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="text-xs">
                      <span className="block text-muted-foreground font-medium mb-1">Reports per 24h · signed-in account</span>
                      <input
                        type="number"
                        min={1}
                        max={1000}
                        value={launchMode.fair_use_per_24h_account ?? 10}
                        onChange={(e) => setLaunchMode({ ...launchMode, fair_use_per_24h_account: Number(e.target.value) })}
                        data-testid="launch-fair-use-account"
                        className="w-full border border-border px-2 py-1.5 text-xs font-mono"
                      />
                    </label>
                    <label className="text-xs">
                      <span className="block text-muted-foreground font-medium mb-1">Reports per 1h · guest</span>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        value={launchMode.fair_use_per_1h_guest ?? 3}
                        onChange={(e) => setLaunchMode({ ...launchMode, fair_use_per_1h_guest: Number(e.target.value) })}
                        data-testid="launch-fair-use-guest"
                        className="w-full border border-border px-2 py-1.5 text-xs font-mono"
                      />
                    </label>
                  </div>
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={async () => {
                        setLaunchSaving(true);
                        try {
                          const fresh = await updatePromoMode({
                            fair_use_per_24h_account: launchMode.fair_use_per_24h_account,
                            fair_use_per_1h_guest: launchMode.fair_use_per_1h_guest,
                          });
                          setLaunchMode(fresh);
                          toast.success("Fair-use limits updated.");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Could not save.");
                        } finally { setLaunchSaving(false); }
                      }}
                      disabled={launchSaving}
                      data-testid="launch-save-fair-use-btn"
                      className="bg-emerald-600 text-white px-3 py-1.5 text-xs hover:bg-emerald-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {launchSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save fair-use
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Grandfather batch — Ultra Pro Beta cohort auto-issuance */}
            <div className="bg-gradient-to-br from-indigo-50 via-white to-amber-50 border border-indigo-200 p-6 mb-6" data-testid="grandfather-batch-card">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
                  <Sparkles size={16} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Grandfather the Ultra Pro Beta cohort</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Auto-issue a unique <span className="font-semibold">lifetime Ultra Pro</span> code to every current Pro subscriber. Idempotent — safe to preview and re-run. Codes are pre-assigned per user; each is single-use.
                  </p>
                </div>
              </div>

              {gfPreview && (
                <div className="bg-white border border-indigo-100 px-4 py-3 mb-3 text-sm" data-testid="gf-preview">
                  <p className="font-semibold text-indigo-800 mb-1">Preview</p>
                  <p className="text-muted-foreground">
                    <span className="font-mono text-foreground">{gfPreview.matches}</span> match{gfPreview.matches === 1 ? "" : "es"} ·{" "}
                    <span className="font-mono text-foreground">{gfPreview.already_issued}</span> already issued ·{" "}
                    <span className="font-mono text-emerald-700 font-semibold">{gfPreview.will_issue}</span> new code{gfPreview.will_issue === 1 ? "" : "s"} will be minted
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button onClick={previewGrandfather} disabled={gfBusy} data-testid="gf-preview-btn"
                  className="border border-indigo-300 text-indigo-800 px-4 py-2 text-sm hover:bg-indigo-50 transition-colors flex items-center gap-2">
                  {gfBusy ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />} Preview
                </button>
                <button onClick={runGrandfather} disabled={gfBusy || !gfPreview || gfPreview.will_issue === 0} data-testid="gf-run-btn"
                  className="bg-indigo-600 text-white px-4 py-2 text-sm hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:opacity-50">
                  {gfBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Issue lifetime Ultra Pro codes
                </button>
              </div>
            </div>

            <div className="bg-card border border-border p-6 mb-8" data-testid="promo-create">
              <p className="overline text-muted-foreground mb-1 flex items-center gap-1.5"><Ticket size={14} /> Create promo code</p>
              <p className="text-sm text-muted-foreground mb-4">Grant Pro time, credits, or Ultra Pro access — for beta testers, ambassadors, university partners, competitions, or goodwill. Leave the code blank to auto-generate one.</p>

              {/* Benefit type selector */}
              <div className="mb-4">
                <label className="overline text-muted-foreground mb-2 block">Benefit</label>
                <div className="inline-flex border border-border flex-wrap" data-testid="promo-benefit-toggle">
                  {[
                    { id: "pro_days", label: "Pro subscription" },
                    { id: "credits", label: "Analysis credits" },
                    { id: "lifetime_ultra_pro", label: "Lifetime Ultra Pro" },
                    { id: "fixed_term_ultra_pro", label: "Fixed-term Ultra Pro" },
                    { id: "percentage_discount", label: "% discount" },
                  ].map((b) => (
                    <button key={b.id} type="button" onClick={() => setPromoForm({ ...promoForm, benefit_type: b.id })}
                      data-testid={`promo-benefit-${b.id}`}
                      className={`px-3.5 py-1.5 text-sm ${promoForm.benefit_type === b.id ? "bg-oxford text-white" : "bg-background text-muted-foreground hover:text-foreground"}`}>{b.label}</button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                <div className="lg:col-span-2">
                  <label className="overline text-muted-foreground">Code (optional)</label>
                  <input value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value })}
                    data-testid="promo-code-input" placeholder="Auto-generate"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                </div>

                {promoForm.benefit_type === "pro_days" && (
                  <div>
                    <label className="overline text-muted-foreground">Duration (days)</label>
                    <input type="number" min="1" max="3650" value={promoForm.days}
                      onChange={(e) => setPromoForm({ ...promoForm, days: e.target.value })}
                      data-testid="promo-days-input"
                      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                    <p className="text-[11px] text-muted-foreground mt-1">7=week · 30=month · 90=quarter · 365=year</p>
                  </div>
                )}
                {promoForm.benefit_type === "credits" && (
                  <div>
                    <label className="overline text-muted-foreground">Credits</label>
                    <input type="number" min="1" max="10000" value={promoForm.credits}
                      onChange={(e) => setPromoForm({ ...promoForm, credits: e.target.value })}
                      data-testid="promo-credits-input"
                      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                    <p className="text-[11px] text-muted-foreground mt-1">Each credit = 1 full examiner report</p>
                  </div>
                )}
                {promoForm.benefit_type === "fixed_term_ultra_pro" && (
                  <div>
                    <label className="overline text-muted-foreground">Term (days)</label>
                    <input type="number" min="1" max="3650" value={promoForm.fixed_term_days}
                      onChange={(e) => setPromoForm({ ...promoForm, fixed_term_days: e.target.value })}
                      data-testid="promo-fixedterm-input"
                      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                    <p className="text-[11px] text-muted-foreground mt-1">Days of Ultra Pro access</p>
                  </div>
                )}
                {promoForm.benefit_type === "percentage_discount" && (
                  <div>
                    <label className="overline text-muted-foreground">Discount %</label>
                    <input type="number" min="1" max="100" value={promoForm.discount_pct}
                      onChange={(e) => setPromoForm({ ...promoForm, discount_pct: e.target.value })}
                      data-testid="promo-discount-input"
                      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                    <p className="text-[11px] text-muted-foreground mt-1">Applied at future checkout</p>
                  </div>
                )}
                {promoForm.benefit_type === "lifetime_ultra_pro" && (
                  <div className="flex items-end">
                    <div className="text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-100 px-3 py-2 w-full">
                      Grants permanent Ultra Pro. No expiry.
                    </div>
                  </div>
                )}

                <div>
                  <label className="overline text-muted-foreground">Max uses</label>
                  <input type="number" min="1" value={promoForm.max_uses} onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                    data-testid="promo-maxuses-input" className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                  <p className="text-[11px] text-muted-foreground mt-1">Set to 1 for single-use</p>
                </div>

                <div>
                  <label className="overline text-muted-foreground">Expires (optional)</label>
                  <input type="date" value={promoForm.expires_at ? promoForm.expires_at.slice(0, 10) : ""}
                    onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value ? `${e.target.value}T23:59:59Z` : "" })}
                    data-testid="promo-expires-input"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                </div>

                <div>
                  <label className="overline text-muted-foreground">Campaign</label>
                  <input value={promoForm.campaign} onChange={(e) => setPromoForm({ ...promoForm, campaign: e.target.value })}
                    data-testid="promo-campaign-input" placeholder="manual"
                    list="promo-campaign-suggest"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono lowercase" />
                  <datalist id="promo-campaign-suggest">
                    <option value="manual" />
                    <option value="student_ambassador" />
                    <option value="university_partnership" />
                    <option value="referral" />
                    <option value="competition" />
                    <option value="goodwill" />
                    <option value="grandfather_ultra_pro_beta" />
                  </datalist>
                </div>

                <div>
                  <label className="overline text-muted-foreground">Assign to email (optional)</label>
                  <input value={promoForm.assigned_email} onChange={(e) => setPromoForm({ ...promoForm, assigned_email: e.target.value })}
                    data-testid="promo-assigned-input" placeholder="student@uni.ac.uk"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono lowercase" />
                  <p className="text-[11px] text-muted-foreground mt-1">Only this user can redeem</p>
                </div>

                <div className="lg:col-span-2">
                  <label className="overline text-muted-foreground">Note</label>
                  <input value={promoForm.note} onChange={(e) => setPromoForm({ ...promoForm, note: e.target.value })}
                    data-testid="promo-note-input" placeholder="e.g. Beta cohort A, Cambridge partnership Q1"
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
                </div>

                <button onClick={submitPromo} disabled={creatingPromo} data-testid="promo-create-btn"
                  className="bg-oxford text-white py-2 text-sm font-medium hover:bg-[#2C5282] transition-colors flex items-center justify-center gap-2 h-[38px] lg:col-span-1">
                  {creatingPromo ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create
                </button>
              </div>
            </div>

            {!promo ? <div className="flex items-center gap-2 text-muted-foreground py-6"><Loader2 className="animate-spin" size={18} /> Loading…</div> : (
              <>
                <div className="grid sm:grid-cols-3 gap-px bg-border border border-border mb-4">
                  <Stat icon={Ticket} label="Total codes" value={promo.summary.total_codes} sub={`${promo.summary.active_codes} active`} testId="promo-stat-total" />
                  <Stat icon={Check} label="Active codes" value={promo.summary.active_codes} sub="redeemable now" testId="promo-stat-active" />
                  <Stat icon={Users} label="Redemptions" value={promo.summary.total_redemptions} sub="total codes used" testId="promo-stat-redemptions" />
                </div>

                {/* Campaign breakdown */}
                {promo.summary.by_campaign && promo.summary.by_campaign.length > 0 && (
                  <div className="bg-card border border-border p-4 mb-4" data-testid="campaign-summary">
                    <p className="overline text-muted-foreground mb-2">By campaign</p>
                    <div className="flex flex-wrap gap-2">
                      {promo.summary.by_campaign.map((c) => (
                        <button key={c.campaign}
                          onClick={() => { setCampaignFilter(c.campaign); getAdminPromo(c.campaign); }}
                          data-testid={`campaign-chip-${c.campaign}`}
                          className={`text-xs px-2.5 py-1 border transition-colors ${campaignFilter === c.campaign ? "bg-oxford text-white border-oxford" : "bg-background border-border hover:bg-secondary"}`}>
                          {c.campaign} <span className="font-mono opacity-70 ml-1">{c.total}</span>
                        </button>
                      ))}
                      {campaignFilter && (
                        <button onClick={() => { setCampaignFilter(""); getAdminPromo(""); }}
                          data-testid="campaign-clear"
                          className="text-xs px-2.5 py-1 border border-dashed border-border hover:bg-secondary">
                          Clear filter
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <p className="overline text-muted-foreground mb-2">Codes{campaignFilter && <> · filtered by <span className="text-foreground font-mono">{campaignFilter}</span></>}</p>
                <div className="overflow-x-auto border border-border mb-8">
                  <table className="w-full text-[13px]" data-testid="promo-codes-table">
                    <thead className="bg-secondary text-muted-foreground">
                      <tr className="text-left">
                        {["Code", "Benefit", "Campaign", "Assigned", "Uses", "Expires", "Note", "Created", "Status", ""].map((h) => (
                          <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {promo.codes.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-muted-foreground">No promo codes yet.</td></tr>}
                      {promo.codes.map((c) => {
                        const bt = c.benefit_type || "pro_days";
                        let benefit = "";
                        let tag = "pro";
                        let tagCls = "border-oxford text-oxford bg-secondary";
                        if (bt === "credits") { benefit = `${c.credits || 0} credit${(c.credits || 0) === 1 ? "" : "s"}`; tag = "credits"; tagCls = "border-amber-400 text-amber-800 bg-amber-50"; }
                        else if (bt === "lifetime_ultra_pro") { benefit = "Lifetime Ultra Pro"; tag = "lifetime"; tagCls = "border-indigo-400 text-indigo-800 bg-indigo-50"; }
                        else if (bt === "fixed_term_ultra_pro") { benefit = `${c.fixed_term_days || 0} days Ultra Pro`; tag = "ultra"; tagCls = "border-indigo-300 text-indigo-700 bg-indigo-50"; }
                        else if (bt === "percentage_discount") { benefit = `${c.discount_pct || 0}% discount`; tag = "discount"; tagCls = "border-emerald-400 text-emerald-800 bg-emerald-50"; }
                        else { benefit = `${c.days || 0} day${(c.days || 0) === 1 ? "" : "s"} Pro`; }
                        const exhausted = (c.uses || 0) >= (c.max_uses || 1);
                        return (
                          <tr key={c.code} className="border-t border-border" data-testid={`promo-row-${c.code}`}>
                            <td className="px-3 py-2 font-mono font-semibold text-oxford">{c.code}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 border ${tagCls}`}>{tag}</span>
                              <span className="ml-2">{benefit}</span>
                            </td>
                            <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground whitespace-nowrap">{c.campaign || "manual"}</td>
                            <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground max-w-[180px] truncate" title={c.assigned_email || ""}>{c.assigned_email || "—"}</td>
                            <td className="px-3 py-2 font-mono">{c.uses}/{c.max_uses}{exhausted && <span className="ml-1 text-[10px] text-muted-foreground">FULL</span>}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{c.expires_at ? fmtDate(c.expires_at) : "—"}</td>
                            <td className="px-3 py-2 max-w-[140px] truncate" title={c.note}>{c.note || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{fmtDate(c.created_at)}</td>
                            <td className="px-3 py-2">
                              <button onClick={() => flipPromo(c.code, !c.active)} data-testid={`promo-toggle-${c.code}`}
                                className={`text-xs px-2 py-0.5 border ${c.active ? "border-[#15803D] text-[#15803D]" : "border-border text-muted-foreground"}`}>
                                {c.active ? "Active" : "Disabled"}
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              <button onClick={() => removePromo(c.code)} data-testid={`promo-delete-${c.code}`} title="Delete"
                                className="text-muted-foreground hover:text-[#B91C1C]"><Trash2 size={13} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <p className="overline text-muted-foreground mb-2">Redemption history</p>
                <div className="overflow-x-auto border border-border">
                  <table className="w-full text-[13px]" data-testid="promo-redemptions-table">
                    <thead className="bg-secondary text-muted-foreground">
                      <tr className="text-left">
                        {["Code", "User", "Granted", "Redeemed"].map((h) => (
                          <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {promo.redemptions.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">No redemptions yet.</td></tr>}
                      {promo.redemptions.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-3 py-2 font-mono">{r.code}</td>
                          <td className="px-3 py-2 max-w-[220px] truncate" title={r.email}>{r.email}</td>
                          <td className="px-3 py-2">{r.months} month{r.months === 1 ? "" : "s"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.redeemed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ---------------- USERS ---------------- */}
        {tab === "Users" && (
          <div data-testid="admin-users">
            {!usersData ? <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> Loading…</div> : (
              <>
                <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                  <p className="text-sm text-muted-foreground">{usersData.total} registered users</p>
                  <input
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    placeholder="Filter by email, name or user_id…"
                    data-testid="users-filter"
                    className="border border-border px-2 py-1 text-sm w-[280px] max-w-full"
                  />
                </div>
                <div className="overflow-x-auto border border-border">
                  <table className="w-full text-[13px]" data-testid="users-table">
                    <thead className="bg-secondary text-muted-foreground">
                      <tr className="text-left">
                        {["Email", "Name", "Role", "Plan", "Pro expires", "Credits", "Analyses", "Provider", "Joined", "Actions"].map((h) => (
                          <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {usersData.users
                        .filter((u) => {
                          const q = userFilter.trim().toLowerCase();
                          if (!q) return true;
                          return [u.email, u.name, u.user_id].some((v) => (v || "").toLowerCase().includes(q));
                        })
                        .map((u) => {
                          const proActive = u.pro_until && new Date(u.pro_until) > new Date();
                          return (
                            <tr key={u.user_id} className="border-t border-border hover:bg-secondary/40">
                              <td className="px-3 py-2 max-w-[220px] truncate" title={u.email}>{u.email}</td>
                              <td className="px-3 py-2">{u.name || "—"}</td>
                              <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 border ${u.role === "admin" ? "border-oxford text-oxford" : "border-border text-muted-foreground"}`}>{u.role}</span></td>
                              <td className="px-3 py-2">{u.plan === "pro" ? <span className="text-[#15803D] font-medium">Pro</span> : "Free"}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-muted-foreground" title={u.pro_until || ""}>
                                {u.pro_until ? (proActive ? fmtDate(u.pro_until) : <span className="line-through opacity-60">{fmtDate(u.pro_until)}</span>) : "—"}
                              </td>
                              <td className="px-3 py-2 font-mono">{u.credits}</td>
                              <td className="px-3 py-2 font-mono">{u.analyses}</td>
                              <td className="px-3 py-2 text-muted-foreground">{u.auth_provider || "—"}</td>
                              <td className="px-3 py-2 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <button
                                  onClick={() => setProModalUser(u)}
                                  data-testid={`manage-pro-${u.user_id}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-oxford text-oxford hover:bg-oxford/5 font-medium"
                                >
                                  <ShieldCheck size={12} />Manage Pro
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                {proModalUser && (
                  <UserProModal
                    user={proModalUser}
                    onClose={() => setProModalUser(null)}
                    onChanged={() => getAdminUsers().then(setUsersData).catch(() => {})}
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* ---------------- PROVIDERS ---------------- */}
        {tab === "Search" && (
          <div data-testid="admin-search">
            <div className="bg-gradient-to-br from-indigo-50 via-white to-amber-50 border border-indigo-200 p-6 mb-6" data-testid="search-hero">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-600 to-amber-500 flex items-center justify-center text-white shrink-0"><Sparkles size={18} /></div>
                <div>
                  <p className="overline text-indigo-700">Submission Intelligence</p>
                  <p className="font-serif text-2xl text-foreground mt-1">Search Provider Layer</p>
                  <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Modular search providers powering &quot;Research &amp; Check Submission&quot;. Enable providers independently, tighten routing sensitivity, and cap per-user usage without touching code.
                  </p>
                </div>
              </div>
            </div>

            {/* Provider health */}
            <div className="bg-card border border-border p-6 mb-6" data-testid="search-providers-health">
              <p className="overline text-muted-foreground mb-3">Provider health</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {(searchHealth?.providers || []).map((p) => (
                  <div key={p.id} className={`border p-4 ${p.enabled ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid={`sp-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-foreground">{p.name}</p>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${p.enabled ? "border-emerald-400 text-emerald-800" : "border-rose-400 text-rose-800"}`}>
                        {p.enabled ? "enabled" : "disabled"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Responsibility: {p.kind.replace(/_/g, " ")}</p>
                    <p className="text-[11px] font-mono text-muted-foreground mt-2">key: {p.key_configured ? "configured ✓" : "MISSING ✗"}</p>
                  </div>
                ))}
                {!searchHealth && <div className="col-span-2 flex items-center gap-2 text-muted-foreground py-4"><Loader2 className="animate-spin" size={16} /> Loading…</div>}
              </div>
            </div>

            {/* Budget config */}
            {searchBudget && (
              <div className="bg-card border border-border p-6 mb-6" data-testid="search-budget-form">
                <p className="overline text-muted-foreground mb-1">Spending controls</p>
                <p className="text-sm text-muted-foreground mb-4">Set any cap to 0 to disable that limit. Set enabled to false to kill-switch the whole feature.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FlagRow label="Feature enabled" desc="Master kill-switch for Submission Intelligence."
                    on={!!searchBudget.enabled} onToggle={() => setSearchBudget({ ...searchBudget, enabled: !searchBudget.enabled })} testId="sb-enabled" />
                  <div>
                    <label className="overline text-muted-foreground">Router sensitivity (min_signal)</label>
                    <input type="number" step="0.05" min="0.1" max="0.9" value={searchBudget.min_signal ?? 0.35}
                      onChange={(e) => setSearchBudget({ ...searchBudget, min_signal: parseFloat(e.target.value) })}
                      data-testid="sb-min-signal"
                      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                    <p className="text-[11px] text-muted-foreground mt-1">Lower = more searches. Higher = only strongest signals.</p>
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">Brave max / submission</label>
                    <input type="number" min="0" max="100" value={searchBudget.brave_max_per_submission ?? 15}
                      onChange={(e) => setSearchBudget({ ...searchBudget, brave_max_per_submission: parseInt(e.target.value) || 0 })}
                      data-testid="sb-brave-max"
                      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">Exa max / submission</label>
                    <input type="number" min="0" max="100" value={searchBudget.exa_max_per_submission ?? 15}
                      onChange={(e) => setSearchBudget({ ...searchBudget, exa_max_per_submission: parseInt(e.target.value) || 0 })}
                      data-testid="sb-exa-max"
                      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">Per user / day</label>
                    <input type="number" min="0" max="1000" value={searchBudget.per_user_per_day ?? 20}
                      onChange={(e) => setSearchBudget({ ...searchBudget, per_user_per_day: parseInt(e.target.value) || 0 })}
                      data-testid="sb-day"
                      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                    <p className="text-[11px] text-muted-foreground mt-1">0 = no daily cap</p>
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">Per user / month</label>
                    <input type="number" min="0" max="10000" value={searchBudget.per_user_per_month ?? 200}
                      onChange={(e) => setSearchBudget({ ...searchBudget, per_user_per_month: parseInt(e.target.value) || 0 })}
                      data-testid="sb-month"
                      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                    <p className="text-[11px] text-muted-foreground mt-1">0 = no monthly cap</p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    setSavingBudget(true);
                    try {
                      const r = await axios.post(`${API}/admin/search-budget`, searchBudget, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } });
                      setSearchBudget(r.data);
                      toast.success("Budget saved.");
                    } catch { toast.error("Could not save budget."); }
                    finally { setSavingBudget(false); }
                  }}
                  disabled={savingBudget}
                  data-testid="sb-save"
                  className="mt-5 bg-oxford text-white px-5 py-2 text-sm font-medium hover:bg-[#2C5282] transition-colors flex items-center gap-2">
                  {savingBudget ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save budget
                </button>
              </div>
            )}

            {/* Usage table */}
            <div className="bg-card border border-border p-6" data-testid="search-usage-table">
              <p className="overline text-muted-foreground mb-1">Top users (last 30 days)</p>
              <p className="text-sm text-muted-foreground mb-4">Ranked by total search calls dispatched. Helps spot runaway usage before it hits the cap.</p>
              {!searchUsage ? <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</div> : (
                <div className="overflow-x-auto border border-border">
                  <table className="w-full text-[13px]">
                    <thead className="bg-secondary text-muted-foreground">
                      <tr className="text-left">{["Email", "Brave", "Exa", "Total"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {(searchUsage.users || []).length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">No searches yet.</td></tr>}
                      {(searchUsage.users || []).map((u) => (
                        <tr key={u.user_id} className="border-t border-border">
                          <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                          <td className="px-3 py-2 font-mono">{u.brave || 0}</td>
                          <td className="px-3 py-2 font-mono">{u.exa || 0}</td>
                          <td className="px-3 py-2 font-mono font-semibold">{u.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "Health" && (
          <div data-testid="admin-health">
            {!healthData ? (
              <div className="flex items-center gap-2 text-muted-foreground py-6"><Loader2 className="animate-spin" size={18} /> Loading system health…</div>
            ) : (
              <>
                {/* Status banner */}
                <div className={`border p-5 mb-6 flex items-center justify-between ${
                  healthData.status === "healthy" ? "border-emerald-200 bg-emerald-50"
                  : healthData.status === "degraded" ? "border-amber-200 bg-amber-50"
                  : "border-rose-300 bg-rose-50"
                }`} data-testid="health-status-banner">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${
                      healthData.status === "healthy" ? "bg-emerald-500 animate-pulse"
                      : healthData.status === "degraded" ? "bg-amber-500 animate-pulse"
                      : "bg-rose-500 animate-pulse"
                    }`}></div>
                    <div>
                      <p className={`font-serif text-2xl capitalize ${
                        healthData.status === "healthy" ? "text-emerald-800"
                        : healthData.status === "degraded" ? "text-amber-900" : "text-rose-900"
                      }`} data-testid="health-status">{healthData.status}</p>
                      <p className="text-xs text-muted-foreground">
                        {healthData.environment} · {healthData.server_name} · uptime {Math.floor(healthData.uptime_seconds / 60)}m
                      </p>
                    </div>
                  </div>
                  <button onClick={() => axios.get(`${API}/admin/health`, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } }).then((r) => setHealthData(r.data))} data-testid="health-refresh"
                    className="text-xs px-3 py-1.5 border border-current hover:bg-white/40">Refresh</button>
                </div>

                {/* KPIs */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-6">
                  <Stat icon={Database} label="Database" value={healthData.db?.ok ? "OK" : "DOWN"} sub={`${healthData.db?.latency_ms ?? "—"} ms ping`} testId="health-db" />
                  <Stat icon={Users} label="Active users" value={healthData.active_users_15m} sub="last 15 min" testId="health-active-users" />
                  <Stat icon={Clock} label="Avg processing" value={healthData.avg_processing_seconds_24h != null ? `${healthData.avg_processing_seconds_24h}s` : "—"} sub="last 24h" testId="health-proc-time" />
                  <Stat icon={AlertTriangle} label="Incidents 24h" value={Object.values(healthData.incidents_24h || {}).reduce((a, b) => a + b, 0)} sub={`${healthData.incidents_24h?.critical || 0} critical`} testId="health-incidents-count" />
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Resource usage */}
                  <div className="bg-card border border-border p-6" data-testid="health-resources">
                    <p className="overline text-muted-foreground mb-3">Server resources</p>
                    {["cpu_pct", "memory_pct", "disk_pct"].map((k) => {
                      const v = healthData.resources?.[k];
                      const label = { cpu_pct: "CPU", memory_pct: "Memory", disk_pct: "Disk" }[k];
                      const tone = v == null ? "bg-zinc-300" : v > 85 ? "bg-rose-500" : v > 70 ? "bg-amber-500" : "bg-emerald-500";
                      return (
                        <div key={k} className="mb-3 last:mb-0">
                          <div className="flex justify-between text-xs mb-1"><span className="font-medium">{label}</span><span className="font-mono">{v != null ? `${v.toFixed(1)}%` : "—"}</span></div>
                          <div className="w-full h-2 bg-zinc-100"><div className={`h-2 ${tone}`} style={{ width: `${Math.min(100, v || 0)}%` }}></div></div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Provider status */}
                  <div className="bg-card border border-border p-6" data-testid="health-providers">
                    <p className="overline text-muted-foreground mb-3">External providers</p>
                    {(healthData.providers || []).length === 0 && <p className="text-sm text-muted-foreground">No providers registered.</p>}
                    {(healthData.providers || []).map((p) => (
                      <div key={p.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div><p className="text-sm font-medium">{p.name}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.kind.replace(/_/g, " ")}</p></div>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${p.enabled ? "border-emerald-400 text-emerald-800 bg-emerald-50" : "border-rose-400 text-rose-800 bg-rose-50"}`}>{p.enabled ? "OK" : "DOWN"}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent incidents */}
                <div className="bg-card border border-border p-6" data-testid="health-incidents">
                  <p className="overline text-muted-foreground mb-3">Recent incidents</p>
                  {(healthData.recent_incidents || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No incidents yet — quiet is good.</p>
                  ) : (
                    <div className="overflow-x-auto border border-border">
                      <table className="w-full text-[13px]">
                        <thead className="bg-secondary text-muted-foreground">
                          <tr className="text-left">{["Time", "Sev", "Feature", "Stage", "Reason", "Message", "Req ID"].map((h) => <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {healthData.recent_incidents.map((i) => (
                            <tr key={i.id} className="border-t border-border" data-testid={`incident-row-${i.id}`}>
                              <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{new Date(i.at).toLocaleString()}</td>
                              <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${
                                i.severity === "critical" ? "border-rose-400 text-rose-800 bg-rose-50"
                                : i.severity === "warning" ? "border-amber-400 text-amber-800 bg-amber-50"
                                : "border-zinc-400 text-zinc-700 bg-zinc-50"
                              }`}>{i.severity}</span></td>
                              <td className="px-3 py-2 font-mono text-xs">{i.feature}</td>
                              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{i.stage || "—"}</td>
                              <td className="px-3 py-2 font-mono text-xs">{i.reason}</td>
                              <td className="px-3 py-2 max-w-[300px] truncate" title={i.message}>{i.message}</td>
                              <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{i.request_id || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-3">Critical incidents email <span className="font-mono">hello@myae.co.uk</span> immediately. Warnings dedupe within a 15-minute window.</p>
                </div>

                {/* Turnitin Calibration */}
                <div className="mt-6 bg-card border border-border p-6" data-testid="admin-turnitin-calibration">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="overline text-muted-foreground">Turnitin calibration</p>
                      <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Upload real Turnitin Similarity Reports to benchmark MyAE&apos;s similarity engine. We record the delta between MyAE&apos;s prediction and Turnitin&apos;s % without ever needing Turnitin&apos;s proprietary database — over time, MAE trends down as we tune the scoring formula.
                      </p>
                    </div>
                    {turnitinBenchmarks && (
                      <div className="text-right shrink-0 ml-4">
                        <p className="text-3xl font-serif font-semibold text-oxford" data-testid="calibration-mae">
                          {turnitinBenchmarks.calibration?.mean_absolute_error != null ? `${turnitinBenchmarks.calibration.mean_absolute_error}pp` : "—"}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">MAE ({turnitinBenchmarks.scored || 0} scored)</p>
                        <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                          bias: {turnitinBenchmarks.calibration?.mean_bias != null ? `${turnitinBenchmarks.calibration.mean_bias >= 0 ? "+" : ""}${turnitinBenchmarks.calibration.mean_bias}pp` : "—"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Band breakdown */}
                  {turnitinBenchmarks?.calibration?.by_band && Object.keys(turnitinBenchmarks.calibration.by_band).length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4" data-testid="calibration-bands">
                      {Object.entries(turnitinBenchmarks.calibration.by_band).map(([band, n]) => (
                        <span key={band} className={`text-xs px-2 py-0.5 border ${
                          band === "excellent" ? "border-emerald-400 text-emerald-800 bg-emerald-50"
                          : band === "good" ? "border-indigo-400 text-indigo-800 bg-indigo-50"
                          : band === "acceptable" ? "border-amber-400 text-amber-800 bg-amber-50"
                          : "border-rose-400 text-rose-800 bg-rose-50"
                        }`}>{band}: <span className="font-mono">{n}</span></span>
                      ))}
                    </div>
                  )}

                  {/* Upload form */}
                  <div className="border border-dashed border-border p-4 mb-4 bg-secondary/40">
                    <p className="text-sm font-medium mb-2">Add a new benchmark</p>
                    <div className="flex flex-wrap gap-2 items-center">
                      <input type="file" accept=".pdf,.docx" id="benchmark-file" data-testid="benchmark-file-input"
                        className="text-xs" />
                      <input type="number" min="0" max="100" step="0.1" value={benchmarkMyaePct} onChange={(e) => setBenchmarkMyaePct(e.target.value)}
                        placeholder="MyAE % (optional)"
                        data-testid="benchmark-myae-input"
                        className="border border-border px-2 py-1 text-xs w-40 bg-background font-mono" />
                      <button
                        onClick={async () => {
                          const inp = document.getElementById("benchmark-file");
                          if (!inp.files?.[0]) { toast.error("Choose a Turnitin PDF."); return; }
                          setUploadingBenchmark(true);
                          const fd = new FormData();
                          fd.append("file", inp.files[0]);
                          const url = `${API}/admin/turnitin-benchmark${benchmarkMyaePct ? `?myae_pct=${benchmarkMyaePct}` : ""}`;
                          try {
                            await axios.post(url, fd, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } });
                            toast.success("Benchmark added.");
                            setBenchmarkMyaePct(""); inp.value = "";
                            const r = await axios.get(`${API}/admin/turnitin-benchmarks`, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } });
                            setTurnitinBenchmarks(r.data);
                          } catch (e) {
                            toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Upload failed.");
                          } finally { setUploadingBenchmark(false); }
                        }}
                        disabled={uploadingBenchmark}
                        data-testid="benchmark-upload-btn"
                        className="text-xs px-3 py-1.5 bg-oxford text-white hover:bg-[#2C5282] flex items-center gap-1">
                        {uploadingBenchmark ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add benchmark
                      </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2">Author name + submission ID are automatically anonymised.</p>
                  </div>

                  {/* Benchmark table */}
                  {turnitinBenchmarks && (turnitinBenchmarks.benchmarks || []).length > 0 && (
                    <div className="overflow-x-auto border border-border" data-testid="benchmark-table">
                      <table className="w-full text-[13px]">
                        <thead className="bg-secondary text-muted-foreground">
                          <tr className="text-left">
                            {["Uploaded", "Turnitin", "MyAE", "Δ", "Band", "Sources", "Filename", ""].map((h) => (
                              <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {turnitinBenchmarks.benchmarks.map((b) => (
                            <tr key={b.id} className="border-t border-border" data-testid={`benchmark-row-${b.id}`}>
                              <td className="px-3 py-2 text-muted-foreground whitespace-nowrap text-xs">{new Date(b.uploaded_at).toLocaleDateString()}</td>
                              <td className="px-3 py-2 font-mono font-semibold">{b.turnitin_pct}%</td>
                              <td className="px-3 py-2 font-mono">{b.myae_pct != null ? `${b.myae_pct}%` : "—"}</td>
                              <td className="px-3 py-2 font-mono">{b.delta != null ? `${b.delta >= 0 ? "+" : ""}${b.delta}` : "—"}</td>
                              <td className="px-3 py-2">
                                {b.band && <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${
                                  b.band === "excellent" ? "border-emerald-400 text-emerald-800 bg-emerald-50"
                                  : b.band === "good" ? "border-indigo-400 text-indigo-800 bg-indigo-50"
                                  : b.band === "acceptable" ? "border-amber-400 text-amber-800 bg-amber-50"
                                  : "border-rose-400 text-rose-800 bg-rose-50"
                                }`}>{b.band}</span>}
                              </td>
                              <td className="px-3 py-2 font-mono">{(b.primary_sources || []).length}</td>
                              <td className="px-3 py-2 max-w-[200px] truncate text-xs text-muted-foreground" title={b.anonymised_meta?.filename}>{b.anonymised_meta?.filename || "—"}</td>
                              <td className="px-3 py-2">
                                <button
                                  onClick={async () => {
                                    if (!window.confirm("Delete this benchmark?")) return;
                                    await axios.delete(`${API}/admin/turnitin-benchmarks/${b.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("ae_token")}` } });
                                    setTurnitinBenchmarks({ ...turnitinBenchmarks, benchmarks: turnitinBenchmarks.benchmarks.filter((x) => x.id !== b.id) });
                                  }}
                                  data-testid={`benchmark-delete-${b.id}`}
                                  className="text-muted-foreground hover:text-rose-600"><Trash2 size={12} /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "Providers" && (
          <div className="bg-card border border-border p-6" data-testid="admin-flags">
            <p className="overline text-muted-foreground mb-1">Similarity providers</p>
            <p className="text-sm text-muted-foreground mb-4">Enable or disable providers instantly — no redeploy required.</p>
            <FlagRow label="MyAE corpus" desc="Compare submissions against the opt-in anonymised corpus." disabled={saving}
              on={!!stats.feature_flags.corpus_similarity} onToggle={() => toggle("corpus_similarity")} testId="flag-corpus" />
            <FlagRow label="Public web / internet similarity" desc="Modular provider — enable once a web/commercial provider is configured." disabled={saving}
              on={!!stats.feature_flags.internet_similarity} onToggle={() => toggle("internet_similarity")} testId="flag-internet" />
          </div>
        )}

        {tab === "Benchmark" && <BenchmarkPanel />}

        {tab === "Provenance" && <ProvenancePanel />}
      </div>

      {/* ---------------- MANUAL CALIBRATION RECORD DIALOG ---------------- */}
      {showManualEntry && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => !manualSaving && setShowManualEntry(false)} data-testid="cal-manual-dialog">
          <div className="bg-card border border-border w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <p className="font-serif text-xl flex items-center gap-2"><Plus size={16} className="text-oxford" /> Manual calibration record</p>
              <button onClick={() => setShowManualEntry(false)} disabled={manualSaving} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-muted-foreground">Use for historical assignments that were never run through MyAE. Both the lecturer&apos;s mark and the raw AI prediction are required.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ManualInput label="Assignment title" val={manualForm.assignment_title} onChange={(v) => setManualForm({ ...manualForm, assignment_title: v })} testId="man-title" />
                <ManualInput label="Subject" val={manualForm.subject} onChange={(v) => setManualForm({ ...manualForm, subject: v })} testId="man-subject" placeholder="e.g. Marketing" />
                <ManualInput label="Module" val={manualForm.module} onChange={(v) => setManualForm({ ...manualForm, module: v })} testId="man-module" placeholder="e.g. MN30349" />
                <div>
                  <label className="overline text-muted-foreground">Module level</label>
                  <select value={manualForm.module_level} onChange={(e) => setManualForm({ ...manualForm, module_level: e.target.value })}
                    data-testid="man-level" className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford">
                    <option value="">—</option>
                    <option value="4">Level 4</option>
                    <option value="5">Level 5</option>
                    <option value="6">Level 6</option>
                    <option value="7">Level 7</option>
                  </select>
                </div>
                <ManualInput label="Assessment type" val={manualForm.assessment_type} onChange={(v) => setManualForm({ ...manualForm, assessment_type: v })} testId="man-type" placeholder="Essay, Report, Poster…" />
                <ManualInput label="University" val={manualForm.university} onChange={(v) => setManualForm({ ...manualForm, university: v })} testId="man-uni" />
                <ManualInput label="Faculty" val={manualForm.faculty} onChange={(v) => setManualForm({ ...manualForm, faculty: v })} testId="man-faculty" />
                <ManualInput label="Department" val={manualForm.department} onChange={(v) => setManualForm({ ...manualForm, department: v })} testId="man-dept" />
                <ManualInput label="Lecturer mark (0–100)*" val={manualForm.actual_lecturer_mark} type="number" onChange={(v) => setManualForm({ ...manualForm, actual_lecturer_mark: v })} testId="man-actual" />
                <ManualInput label="MyAE raw prediction (0–100)*" val={manualForm.myae_raw_prediction} type="number" onChange={(v) => setManualForm({ ...manualForm, myae_raw_prediction: v })} testId="man-raw" />
                <ManualInput label="AI confidence (%)" val={manualForm.ai_confidence} type="number" onChange={(v) => setManualForm({ ...manualForm, ai_confidence: v })} testId="man-conf" />
              </div>
              <div>
                <label className="overline text-muted-foreground">Lecturer feedback</label>
                <textarea value={manualForm.lecturer_feedback} onChange={(e) => setManualForm({ ...manualForm, lecturer_feedback: e.target.value })}
                  data-testid="man-feedback" rows={3}
                  className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
              </div>
              <div>
                <label className="overline text-muted-foreground">Notes</label>
                <textarea value={manualForm.notes} onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                  data-testid="man-notes" rows={2}
                  className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowManualEntry(false)} disabled={manualSaving} className="flex-1 border border-border py-2.5 text-sm hover:bg-secondary">Cancel</button>
                <button onClick={saveManualEntry} disabled={manualSaving} data-testid="man-save-btn"
                  className="flex-1 bg-oxford text-white py-2.5 text-sm font-medium hover:bg-[#2C5282] flex items-center justify-center gap-2">
                  {manualSaving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- EDIT CALIBRATION RECORD DIALOG ---------------- */}
      {editingRecord && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => !manualSaving && setEditingRecord(null)} data-testid="cal-edit-dialog">
          <div className="bg-card border border-border w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <p className="font-serif text-xl flex items-center gap-2"><PenSquare size={16} className="text-oxford" /> Edit calibration record</p>
              <button onClick={() => setEditingRecord(null)} disabled={manualSaving} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ManualInput label="Subject" val={editingRecord.subject || ""} onChange={(v) => setEditingRecord({ ...editingRecord, subject: v })} testId="edit-subject" />
                <ManualInput label="Module" val={editingRecord.module || ""} onChange={(v) => setEditingRecord({ ...editingRecord, module: v })} testId="edit-module" />
                <div>
                  <label className="overline text-muted-foreground">Module level</label>
                  <select value={editingRecord.module_level || ""} onChange={(e) => setEditingRecord({ ...editingRecord, module_level: e.target.value })}
                    className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford">
                    <option value="">—</option>
                    <option value="4">Level 4</option>
                    <option value="5">Level 5</option>
                    <option value="6">Level 6</option>
                    <option value="7">Level 7</option>
                  </select>
                </div>
                <ManualInput label="Assessment type" val={editingRecord.assessment_type || ""} onChange={(v) => setEditingRecord({ ...editingRecord, assessment_type: v })} testId="edit-type" />
                <ManualInput label="University" val={editingRecord.university || ""} onChange={(v) => setEditingRecord({ ...editingRecord, university: v })} testId="edit-uni" />
                <ManualInput label="Faculty" val={editingRecord.faculty || ""} onChange={(v) => setEditingRecord({ ...editingRecord, faculty: v })} testId="edit-faculty" />
                <ManualInput label="Department" val={editingRecord.department || ""} onChange={(v) => setEditingRecord({ ...editingRecord, department: v })} testId="edit-dept" />
                <ManualInput label="Lecturer mark" val={editingRecord.actual_lecturer_mark ?? ""} type="number" onChange={(v) => setEditingRecord({ ...editingRecord, actual_lecturer_mark: v })} testId="edit-actual" />
              </div>
              <div>
                <label className="overline text-muted-foreground">Lecturer feedback</label>
                <textarea value={editingRecord.lecturer_feedback || ""} onChange={(e) => setEditingRecord({ ...editingRecord, lecturer_feedback: e.target.value })}
                  rows={3} className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
              </div>
              <div>
                <label className="overline text-muted-foreground">Notes</label>
                <textarea value={editingRecord.notes || ""} onChange={(e) => setEditingRecord({ ...editingRecord, notes: e.target.value })}
                  rows={2} className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setEditingRecord(null)} disabled={manualSaving} className="flex-1 border border-border py-2.5 text-sm hover:bg-secondary">Cancel</button>
                <button onClick={saveRecordEdit} disabled={manualSaving} data-testid="edit-save-btn"
                  className="flex-1 bg-oxford text-white py-2.5 text-sm font-medium hover:bg-[#2C5282] flex items-center justify-center gap-2">
                  {manualSaving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- ACTUAL-MARK DIALOG ---------------- */}
      {mark && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !markSaving && setMark(null)} data-testid="mark-dialog">
          <div className="bg-card border border-border w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <p className="font-serif text-xl flex items-center gap-2"><Target size={16} className="text-oxford" /> Enter actual mark</p>
              <button onClick={() => setMark(null)} disabled={markSaving} data-testid="mark-close-btn" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="p-5">
              <label className="overline text-muted-foreground">University mark (0–100)</label>
              <input type="number" min="0" max="100" autoFocus value={markValue}
                onChange={(e) => { setMarkValue(e.target.value); setMarkErr(""); }}
                onKeyDown={(e) => e.key === "Enter" && saveMark()}
                data-testid="mark-input"
                className="mt-1 w-full border border-border px-3 py-2.5 text-lg font-mono bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
              {markErr && <p className="text-xs text-[#B91C1C] mt-2" data-testid="mark-error">{markErr}</p>}
              <div className="flex gap-2 mt-5">
                <button onClick={() => setMark(null)} disabled={markSaving} className="flex-1 border border-border py-2.5 text-sm hover:bg-secondary transition-colors">Cancel</button>
                <button onClick={saveMark} disabled={markSaving} data-testid="mark-save-btn"
                  className="flex-1 bg-oxford text-white py-2.5 text-sm font-medium hover:bg-[#2C5282] transition-colors flex items-center justify-center gap-2">
                  {markSaving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- PREVIEW IMPACT MODAL ---------------- */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => !previewLoading && !savingVersion && setPreviewOpen(false)} data-testid="cal-preview-modal">
          <div className="bg-card border border-border w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
              <p className="font-serif text-xl flex items-center gap-2"><Eye size={16} className="text-oxford" /> Calibration Preview</p>
              <button onClick={() => setPreviewOpen(false)} disabled={previewLoading || savingVersion} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-5">
              <p className="text-xs text-muted-foreground">Simulate the proposed calibration against every validated record. Nothing is applied until you click &ldquo;Save &amp; activate as new version&rdquo;.</p>

              {/* Adjustments editor */}
              <div>
                <p className="overline text-muted-foreground mb-2">Proposed assessment-type adjustments</p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto border border-border p-3">
                  {Object.entries(previewAdjustments || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-3">
                      <span className="w-40 text-sm capitalize">{k.replace(/_/g, " ")}</span>
                      <input type="number" step="0.5" defaultValue={v}
                        data-testid={`cal-preview-input-${k}`}
                        onBlur={(e) => {
                          const n = parseFloat(e.target.value);
                          if (!isNaN(n)) setPreviewAdjustments({ ...previewAdjustments, [k]: n });
                        }}
                        className="w-24 border border-border px-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford font-mono" />
                      <span className="text-xs text-muted-foreground">pp</span>
                    </div>
                  ))}
                </div>
                <button onClick={refreshPreview} disabled={previewLoading}
                  data-testid="cal-preview-refresh-btn"
                  className="mt-2 inline-flex items-center gap-2 text-xs border border-border px-2.5 py-1 hover:border-oxford hover:text-oxford">
                  {previewLoading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Recompute preview
                </button>
              </div>

              {/* Impact panel */}
              {previewLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-6"><Loader2 className="animate-spin" size={18} /> Computing…</div>
              ) : previewResult ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border" data-testid="cal-preview-impact">
                  <Stat icon={Target} label="Current MAE" value={previewResult.current.mae != null ? `±${previewResult.current.mae}%` : "—"} sub={`${previewResult.current.records} records`} />
                  <Stat icon={Target} label="Projected MAE" value={previewResult.projected.mae != null ? `±${previewResult.projected.mae}%` : "—"} sub={previewResult.delta.mae != null ? `Δ ${previewResult.delta.mae > 0 ? "+" : ""}${previewResult.delta.mae}` : "—"} />
                  <Stat icon={ArrowUp} label="Current signed bias" value={previewResult.current.signed_bias != null ? (previewResult.current.signed_bias > 0 ? `+${previewResult.current.signed_bias}` : previewResult.current.signed_bias) : "—"} sub="+ under-predicting" />
                  <Stat icon={ArrowUp} label="Projected signed bias" value={previewResult.projected.signed_bias != null ? (previewResult.projected.signed_bias > 0 ? `+${previewResult.projected.signed_bias}` : previewResult.projected.signed_bias) : "—"} sub={previewResult.delta.signed_bias != null ? `Δ ${previewResult.delta.signed_bias > 0 ? "+" : ""}${previewResult.delta.signed_bias}` : "—"} />
                  <Stat icon={Database} label="Records affected" value={previewResult.projected.records_affected} sub={`${previewResult.projected.buckets_affected} bucket(s)`} />
                  <Stat icon={TrendingUp} label="Avg grade movement" value={previewResult.projected.avg_movement != null ? `${previewResult.projected.avg_movement > 0 ? "+" : ""}${previewResult.projected.avg_movement}` : "—"} sub={`largest ${previewResult.projected.largest_increase != null ? `+${previewResult.projected.largest_increase}` : "—"} / ${previewResult.projected.largest_decrease != null ? previewResult.projected.largest_decrease : "—"}`} />
                </div>
              ) : null}

              {/* Save controls */}
              <div className="border-t border-border pt-4">
                <p className="overline text-muted-foreground mb-2">Release notes for this version</p>
                <textarea rows={2} value={newVersionNotes} onChange={(e) => setNewVersionNotes(e.target.value)}
                  data-testid="cal-preview-notes"
                  placeholder="e.g. Reduced Report under-prediction by 4% after week-3 validation batch"
                  className="w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => setPreviewOpen(false)} disabled={savingVersion}
                    data-testid="cal-preview-cancel-btn"
                    className="border border-border px-3.5 py-2 text-sm hover:bg-secondary">Cancel</button>
                  <button onClick={() => applyAsNewVersion(false)} disabled={savingVersion || previewLoading}
                    data-testid="cal-preview-save-btn"
                    className="border border-border px-3.5 py-2 text-sm hover:border-oxford hover:text-oxford inline-flex items-center gap-2">
                    {savingVersion ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save (inactive)
                  </button>
                  <button onClick={() => applyAsNewVersion(true)} disabled={savingVersion || previewLoading}
                    data-testid="cal-preview-activate-btn"
                    className="bg-oxford text-white px-3.5 py-2 text-sm hover:bg-[#2C5282] inline-flex items-center gap-2">
                    {savingVersion ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Save &amp; activate
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 italic">Every save creates a new version. Nothing is overwritten.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- DEBUG MODAL ---------------- */}
      {debug && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDebug(null)} data-testid="debug-modal">
          <div className="bg-card border border-border max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
              <p className="font-serif text-2xl flex items-center gap-2"><Bug size={18} className="text-oxford" /> Debug Mode</p>
              <button onClick={() => setDebug(null)} data-testid="debug-close-btn" className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            {debugLoading || !debug.status ? (
              <div className="flex items-center gap-2 text-muted-foreground p-10"><Loader2 className="animate-spin" size={18} /> Loading…</div>
            ) : (
              <div className="p-6 space-y-5 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge>Status: {debug.status}</Badge>
                  {debug.assessment_type && <Badge>Type: {debug.assessment_type}</Badge>}
                  {debug.predicted_grade != null && <Badge>Predicted: {debug.predicted_grade}%</Badge>}
                  {debug.confidence != null && <Badge>Confidence: {debug.confidence}%</Badge>}
                  {debug.prompt_version && <Badge>Prompt: {debug.prompt_version}</Badge>}
                  {debug.calibration_version && <Badge>Cal: {debug.calibration_version}</Badge>}
                  {debug.ai_cost_estimate ? <Badge>Cost: {usd(debug.ai_cost_estimate)}</Badge> : null}
                </div>
                {debug.error && <div className="bg-[#FEF2F2] border border-[#FCA5A5] text-[#991B1B] p-3 text-xs">{debug.error}</div>}

                <Section title="Extraction summary">
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {Object.entries(debug.extraction || {}).map(([k, v]) => (
                      <div key={k} className="bg-secondary border border-border px-2 py-1.5">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</p>
                        <p className="font-mono">{String(v)}</p>
                      </div>
                    ))}
                    {Object.keys(debug.extraction || {}).length === 0 && <p className="text-muted-foreground text-xs">No extraction metadata.</p>}
                  </div>
                </Section>

                <Section title="Documents used"><ChipList items={(debug.documents_used || []).map((d) => `${d.label}${d.used ? "" : " ✗"}`)} /></Section>

                <Section title="Referencing detection">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="bg-secondary border border-border px-2 py-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">References detected</p>
                      <p className={`font-mono font-semibold ${(debug.referencing?.total_references || 0) > 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>
                        {(debug.referencing?.total_references || 0) > 0 ? "Yes" : "No"}
                      </p>
                    </div>
                    <div className="bg-secondary border border-border px-2 py-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground"># references</p>
                      <p className="font-mono">{debug.referencing?.total_references ?? 0}</p>
                    </div>
                    <div className="bg-secondary border border-border px-2 py-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground"># in-text</p>
                      <p className="font-mono">{debug.referencing?.in_text_citations ?? 0}</p>
                    </div>
                    <div className="bg-secondary border border-border px-2 py-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Style</p>
                      <p className="font-mono">{debug.referencing?.detected_style || "—"}</p>
                    </div>
                    {debug.referencing?.reference_location && (
                      <div className="bg-secondary border border-border px-2 py-1.5 col-span-2 sm:col-span-4">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Location</p>
                        <p className="font-mono">
                          {debug.referencing.reference_location.slide ? `Slide ${debug.referencing.reference_location.slide} · ` : ""}
                          {debug.referencing.reference_location.position_pct}% into the document
                        </p>
                      </div>
                    )}
                  </div>
                </Section>

                <Section title="Pipeline telemetry (extraction → LLM)">
                  {debug.text_telemetry && Object.keys(debug.text_telemetry).length > 0 ? (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          ["Characters extracted", debug.text_telemetry.chars_extracted],
                          ["Words extracted", debug.text_telemetry.words_extracted],
                          ["Chars → examiner (essay)", debug.text_telemetry.essay_chars_to_examiner],
                          ["Chars → examiner (prompt)", debug.text_telemetry.chars_to_examiner_prompt],
                          ["Tokens → examiner (est)", debug.text_telemetry.tokens_to_examiner_est],
                          ["Chars → structure", debug.text_telemetry.chars_to_structure],
                          ["Tokens → structure (est)", debug.text_telemetry.tokens_to_structure_est],
                          ["Structure full limit", debug.text_telemetry.structure_full_limit],
                        ].map(([k, v]) => (
                          <div key={k} className="bg-secondary border border-border px-2 py-1.5">
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</p>
                            <p className="font-mono">{v ?? "—"}</p>
                          </div>
                        ))}
                        <div className="bg-secondary border border-border px-2 py-1.5">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Examiner truncated</p>
                          <p className={`font-mono font-semibold ${debug.text_telemetry.examiner_truncated ? "text-[#B91C1C]" : "text-[#15803D]"}`}>
                            {debug.text_telemetry.examiner_truncated ? "Yes" : "No"}
                          </p>
                        </div>
                        <div className="bg-secondary border border-border px-2 py-1.5">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Structure truncated</p>
                          <p className={`font-mono font-semibold ${debug.text_telemetry.structure_truncated ? "text-[#B45309]" : "text-[#15803D]"}`}>
                            {debug.text_telemetry.structure_truncated ? "Yes (head+tail kept)" : "No"}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 p-3 bg-[#F8FAFC] border border-border text-xs" data-testid="ref-root-cause">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Reference root-cause diagnostic</p>
                        {!debug.text_telemetry.ref_heading_in_extracted_text ? (
                          <p className="text-[#B45309]">
                            No reference heading found in the <strong>extracted text</strong> — the references are likely embedded as an image/graphic (no text layer). This is an <strong>OCR/Vision</strong> requirement, not a parser bug.
                          </p>
                        ) : debug.text_telemetry.ref_heading_in_extracted_text && !debug.text_telemetry.references_detected ? (
                          <p className="text-[#B91C1C]">
                            A reference heading IS present in the extracted text but the detector did not identify a list — this is a <strong>detection/parser</strong> issue to fix in <code>checkers.py</code>.
                          </p>
                        ) : (
                          <p className="text-[#15803D]">
                            References present in extracted text and detected correctly ({debug.text_telemetry.total_references} found). Pipeline healthy.
                          </p>
                        )}
                        {debug.text_telemetry.ref_detection_debug?.reason && (
                          <p className="mt-1.5 text-muted-foreground" data-testid="ref-parser-reason">
                            <strong>Parser:</strong> {debug.text_telemetry.ref_detection_debug.reason}
                          </p>
                        )}
                        {debug.text_telemetry.ref_detection_debug?.block_preview && (
                          <pre className="mt-1.5 max-h-28 overflow-auto whitespace-pre-wrap font-mono text-[10px] bg-white border border-border p-2" data-testid="ref-block-preview">{debug.text_telemetry.ref_detection_debug.block_preview}</pre>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-xs">No telemetry recorded (older analysis — re-run to capture).</p>
                  )}
                </Section>

                {debug.supporting_docs?.length > 0 && (
                  <Section title="Supporting documents"><ChipList items={debug.supporting_docs.map((s) => `${s.doc_type}: ${s.filename} (${s.chars} chars)`)} /></Section>
                )}

                {(debug.raw_prediction != null || debug.final_prediction != null || (debug.calibration_meta && Object.keys(debug.calibration_meta).length > 0)) && (
                  <Section title="Calibration layer">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="bg-secondary border border-border px-2 py-1.5">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Raw AI prediction</p>
                        <p className="font-mono">{debug.raw_prediction != null ? `${debug.raw_prediction}%` : "—"}</p>
                      </div>
                      <div className="bg-secondary border border-border px-2 py-1.5">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Applied adjustment</p>
                        <p className="font-mono">{debug.calibration_meta?.applied_adjustment != null ? `${debug.calibration_meta.applied_adjustment > 0 ? "+" : ""}${debug.calibration_meta.applied_adjustment}` : "0"}</p>
                      </div>
                      <div className="bg-secondary border border-border px-2 py-1.5">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Final prediction</p>
                        <p className="font-mono font-semibold text-oxford">{debug.final_prediction != null ? `${debug.final_prediction}%` : "—"}</p>
                      </div>
                      <div className="bg-secondary border border-border px-2 py-1.5">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Layer active</p>
                        <p className={`font-mono font-semibold ${debug.calibration_meta?.active ? "text-[#15803D]" : "text-muted-foreground"}`}>
                          {debug.calibration_meta?.active ? "Yes" : "No"}
                        </p>
                      </div>
                      <div className="bg-secondary border border-border px-2 py-1.5 col-span-2 sm:col-span-4">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Bucket used</p>
                        <p className="font-mono text-xs">{debug.calibration_meta?.bucket || debug.calibration_meta?.reason || "—"}
                          {debug.calibration_meta?.sample_size != null && <span className="text-muted-foreground"> · n={debug.calibration_meta.sample_size}</span>}
                        </p>
                      </div>
                    </div>
                  </Section>
                )}

                {(debug.module || debug.subject || debug.module_level || debug.faculty || debug.department) && (
                  <Section title="Assignment metadata">
                    <ChipList items={[
                      debug.university && `University: ${debug.university}`,
                      debug.faculty && `Faculty: ${debug.faculty}`,
                      debug.department && `Department: ${debug.department}`,
                      debug.module && `Module: ${debug.module}`,
                      debug.subject && `Subject: ${debug.subject}`,
                      debug.module_level && `Level: ${debug.module_level}`,
                    ].filter(Boolean)} />
                  </Section>
                )}

                <Field label="Extracted assignment question" value={debug.extracted_question} />
                <Field label="Extracted learning outcomes" value={debug.extracted_outcomes} />
                <Field label="Extracted marking rubric" value={debug.extracted_rubric} />
                <Field label="Assignment guide" value={debug.assignment_guide} />
                {debug.confidence_reasons?.length > 0 && (
                  <Section title="Confidence reasons">
                    <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1">
                      {debug.confidence_reasons.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </Section>
                )}
                <Field label="Exact prompt sent to the examiner model" value={debug.prompt} mono big />
                <Field label="Extracted submission text" value={debug.extracted_text} mono big />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Badge = ({ children }) => <span className="text-[11px] bg-secondary border border-border px-2 py-1 font-mono">{children}</span>;
const ManualInput = ({ label, val, onChange, testId, type = "text", placeholder }) => (
  <div>
    <label className="overline text-muted-foreground">{label}</label>
    <input type={type} value={val ?? ""} onChange={(e) => onChange(e.target.value)}
      data-testid={testId} placeholder={placeholder}
      className="mt-1 w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
  </div>
);
const ChipList = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.length === 0 ? <span className="text-muted-foreground text-xs">None</span> :
      items.map((it, i) => <span key={i} className="text-xs bg-secondary border border-border px-2 py-0.5">{it}</span>)}
  </div>
);
const Section = ({ title, children }) => (
  <div><p className="overline text-muted-foreground mb-2">{title}</p>{children}</div>
);
const Field = ({ label, value, mono, big }) => (
  <div>
    <p className="overline text-muted-foreground mb-1.5">{label}</p>
    {value ? (
      <pre className={`bg-secondary border border-border p-3 whitespace-pre-wrap break-words ${mono ? "font-mono text-[11px]" : "text-xs"} ${big ? "max-h-64 overflow-y-auto" : ""}`}>{value}</pre>
    ) : <p className="text-muted-foreground text-xs italic">— not provided —</p>}
  </div>
);
